/**
 * HumanoidMesh - Builds a REALISTIC human character with proper facial features.
 * Uses procedural geometry to create detailed humans with faces, not primitive shapes.
 */

import * as BABYLON from "babylonjs";
import { SceneLoader, IAnimatable } from "babylonjs";

export interface HumanoidColors {
  skin: BABYLON.Color3;
  jersey: BABYLON.Color3;
  shorts: BABYLON.Color3;
  boots: BABYLON.Color3;
  hair: BABYLON.Color3;
  eyeColor: BABYLON.Color3;
}

export interface HumanoidLimbs {
  leftArm: BABYLON.TransformNode;
  rightArm: BABYLON.TransformNode;
  leftLeg: BABYLON.TransformNode;
  rightLeg: BABYLON.TransformNode;
  head: BABYLON.Mesh;
  torso: BABYLON.Mesh;
  torsoGroup: BABYLON.TransformNode;
  headGroup: BABYLON.TransformNode;
  leftEyelid: BABYLON.Mesh;
  rightEyelid: BABYLON.Mesh;
}

export interface HumanoidResult {
  root: BABYLON.Mesh;
  limbs: HumanoidLimbs;
}

// FBX Animation result
export interface FBXAnimations {
  idle: BABYLON.AnimationGroup | null;
  walk: BABYLON.AnimationGroup | null;
  run: BABYLON.AnimationGroup | null;
  celebrate: BABYLON.AnimationGroup | null;
  /** Root nodes of the run model instance (to hide during celebration) */
  runRootNodes?: BABYLON.Node[];
  /** Root nodes of the celebrate model instance (to show during celebration) */
  celebrateRootNodes?: BABYLON.Node[];
}

// Load FBX animations from files
export async function loadFBXAnimations(scene: BABYLON.Scene): Promise<FBXAnimations> {
  const animations: FBXAnimations = {
    idle: null,
    walk: null,
    run: null,
    celebrate: null
  };

  try {
    // Load slow run for running animation
    const runResult = await SceneLoader.ImportMeshAsync(
      "",
      "./assets/",
      "slow_run.glb",
      scene
    );
    if (runResult.animationGroups.length > 0) {
      animations.run = runResult.animationGroups[0];
      animations.run.stop();
      animations.run.loopAnimation = true;
    }

    // Load excited for celebration
    const celebrateResult = await SceneLoader.ImportMeshAsync(
      "",
      "./assets/",
      "excited.glb",
      scene
    );
    if (celebrateResult.animationGroups.length > 0) {
      animations.celebrate = celebrateResult.animationGroups[0];
      animations.celebrate.stop();
      animations.celebrate.loopAnimation = true;
    }

    console.log("FBX animations loaded successfully!");
  } catch (e) {
    console.warn("Could not load FBX animations:", e);
  }

  return animations;
}

// Cache for FBX asset containers to avoid re-downloading
let _runContainerPromise: Promise<BABYLON.AssetContainer> | null = null;
let _celebrateContainerPromise: Promise<BABYLON.AssetContainer> | null = null;

/**
 * Load FBX model as the character mesh with skeletal animations.
 * Uses Slow Run.fbx as the base mesh + run animation, Excited.fbx for celebration.
 * Returns a root transform node and animation groups.
 */
export async function loadFBXCharacter(
  name: string,
  scene: BABYLON.Scene,
  colors: Partial<HumanoidColors> = {},
  shadowGenerator?: BABYLON.ShadowGenerator
): Promise<{ root: BABYLON.Mesh; animations: FBXAnimations }> {
  const c = { ...DEFAULT_COLORS, ...colors };

  // Use cached container if available, otherwise load it
  if (!_runContainerPromise) {
    console.log("[DEBUG] Loading actualmodel.glb...");
    _runContainerPromise = SceneLoader.LoadAssetContainerAsync(
      "",
      "./models/actualmodel.glb",
      scene
    );
  }

  if (!_celebrateContainerPromise) {
    _celebrateContainerPromise = SceneLoader.LoadAssetContainerAsync(
      "./assets/",
      "excited.glb",
      scene
    );
  }

  const [runContainer, celebrateContainer] = await Promise.all([
    _runContainerPromise,
    _celebrateContainerPromise,
  ]);

  // Instantiate the run container for this character's mesh + skeleton
  const runInstance = runContainer.instantiateModelsToScene(
    (sourceName) => `${name}_${sourceName}`
  );

  // Create a stable root mesh for positioning
  const root = BABYLON.MeshBuilder.CreateBox(`${name}_root`, { size: 0.01 }, scene);
  root.isVisible = false;

  // Parent all instantiated meshes under the root
  for (const node of runInstance.rootNodes) {
    node.parent = root;
    // FIXED: Removed position.set(0,0,0) as it was burying the number inside the player body
  }

  // Scale and align - GLB models from Blender are usually correctly scaled or in meters
  const allMeshes: BABYLON.AbstractMesh[] = [];
  for (const node of runInstance.rootNodes) {
    if (node instanceof BABYLON.AbstractMesh) {
      allMeshes.push(node);
      console.log(`[DEBUG] Loaded Mesh: ${node.name}, Pos: ${node.position}, Parent: ${node.parent?.name || "None"}`);
    }
    node.getChildMeshes().forEach((m) => {
      allMeshes.push(m);
      // console.log(`[DEBUG]   Sub-mesh: ${m.name}, Parent: ${m.parent?.name || "None"}`);
    });
  }

  // DEBUG: Log all loaded meshes
  console.log("[DEBUG] Loaded meshes:");
  allMeshes.forEach((mesh, i) => {
    console.log(`  [${i}] ${mesh.name} - visible: ${mesh.isVisible}, enabled: ${mesh.isEnabled()}`);
    // Make sure all meshes are visible
    mesh.isVisible = true;
    mesh.setEnabled(true);
  });

  // Fix shader compilation errors from too many bone uniforms:
  // Use texture-based bone storage (removes bone count limit)
  const jerseyMat = new BABYLON.PBRMaterial(`${name}_jersey_red`, scene);
  jerseyMat.albedoColor = c.jersey;
  jerseyMat.roughness = 0.6;
  jerseyMat.metallic = 0.0;
  jerseyMat.sheen.isEnabled = true;
  jerseyMat.backFaceCulling = false;

  const skinMat = new BABYLON.PBRMaterial(`${name}_skin_pale`, scene);
  skinMat.albedoColor = c.skin;
  skinMat.roughness = 0.4;
  skinMat.metallic = 0.0;
  skinMat.subSurface.isScatteringEnabled = true;

  const shortsMat = new BABYLON.PBRMaterial(`${name}_shorts_green`, scene);
  shortsMat.albedoColor = c.shorts;
  shortsMat.roughness = 0.7;

  // DISABLED: Material override - Use GLB materials instead
  // The actualmodel.glb already has proper materials from Blender
  /*
  for (const mesh of allMeshes) {
    if (mesh.skeleton) {
      mesh.skeleton.useTextureToStoreBoneMatrices = true;
    }
    
    const mName = mesh.name.toLowerCase();
    mesh.useVertexColors = false;
    
    // Check if the mesh has multiple material slots (submeshes)
    if (mesh.material instanceof BABYLON.MultiMaterial) {
        const mm = mesh.material;
        for (let i = 0; i < mm.subMaterials.length; i++) {
            const subMat = mm.subMaterials[i];
            if (!subMat) continue;
            const subName = subMat.name.toLowerCase();
            
            // PRIORITY: Specific body parts
            if (subName.includes("skin") || subName.includes("face") || subName.includes("head") || subName.includes("hand") || subName.includes("arm") || subName.includes("finger") || subName.includes("ear") || subName.includes("neck")) {
                mm.subMaterials[i] = skinMat;
            } else if (subName.includes("short") || subName.includes("pant") || subName.includes("leg") || subName.includes("thigh")) {
                mm.subMaterials[i] = shortsMat;
            } else if (subName.includes("jersey") || subName.includes("shirt") || subName.includes("torso") || subName.includes("body") || subName.includes("chest") || subName.includes("abdomen")) {
                mm.subMaterials[i] = jerseyMat;
            } else {
                // Default to skin for unidentified parts
                mm.subMaterials[i] = skinMat;
            }
        }
    } else {
        // Single mesh part - apply based on name
        // PRIORITY: Specific parts first
        if (mName.includes("head") || mName.includes("face") || mName.includes("hand") || mName.includes("skin") || mName.includes("arm") || mName.includes("neck")) {
            mesh.material = skinMat;
        } else if (mName.includes("short") || mName.includes("pant") || mName.includes("thigh")) {
            mesh.material = shortsMat;
        } else if (mName.includes("jersey") || mName.includes("shirt") || mName.includes("torso")) {
            mesh.material = jerseyMat; 
        } else if (mName.includes("body") || mName.includes("ch12")) {
            // Mixed body meshes often default to jersey
            mesh.material = jerseyMat;
        } else if (mName.includes("leg")) {
            mesh.material = shortsMat;
        } else {
            mesh.material = skinMat;
        }
    }
  }
  */

  // Determine spine bone for attachments
  let spineBone: BABYLON.Bone | null = null;
  const mainMesh = allMeshes.find(m => m.name.toLowerCase().includes("ch12") && m.skeleton) as BABYLON.Mesh;
  if (mainMesh && mainMesh.skeleton) {
    const boneSearchNames = ["mixamorig:spine2", "mixamorig:spine1", "spine2", "spine1", "spine"];
    for (const bName of boneSearchNames) {
      const b = mainMesh.skeleton.bones.find(bone => bone.name.toLowerCase().includes(bName));
      if (b) {
        spineBone = b;
        break;
      }
    }
  }

  // Helper to attach floating meshes to bones with correct orientation
  const attachToBody = (floatingMesh: BABYLON.Mesh, y: number, z: number, rotationY: number = Math.PI) => {
    if (spineBone && mainMesh) {
      floatingMesh.attachToBone(spineBone, mainMesh);
      // Tighten offsets for bone space: Mixamo Spine Y is 'up', Z is 'front/back'
      floatingMesh.position.set(0, y, z);
      floatingMesh.rotation.set(0, rotationY, 0);
      // Boost visibility with subtle emissive
      if (floatingMesh.material instanceof BABYLON.PBRMaterial) {
        floatingMesh.material.emissiveColor = new BABYLON.Color3(0.3, 0.3, 0.3);
        floatingMesh.material.disableLighting = false;
      }
    } else {
      const body = allMeshes.find(m => m.name.toLowerCase().includes("ch12"));
      if (body) {
        floatingMesh.parent = body;
        floatingMesh.position.set(0, 1.4 + y, -0.18 + z);
        floatingMesh.rotation.y = rotationY;
      }
    }
  };

  function attachToBack(floatingMesh: BABYLON.Mesh, y: number, z: number) {
    attachToBody(floatingMesh, y, z, Math.PI);
  }

  // HANDLE JERSEY NUMBER
  // 1. Try to find the number mesh in the GLB model
  const numberMeshes = allMeshes.filter(m =>
    m.name.toLowerCase().includes("number") ||
    m.name.toLowerCase().includes("text") ||
    (m.name.toLowerCase().includes("7") && (m.name.length < 10 || m.name.toLowerCase().includes("jersey")))
  );

  let numberFound = false;
  if (numberMeshes.length > 0) {
    numberMeshes.forEach(m => {
      console.log("[DEBUG] Found Number mesh in model:", m.name);
      m.isVisible = true;
      m.setEnabled(true);
      numberFound = true;

      // If it's the player model, ensure it's visible and correctly parented
      if (name === "player") {
        console.log("[DEBUG] Found Number mesh match:", m.name);
        m.isVisible = true;
        m.setEnabled(true);

        // PARENTING LOGIC:
        // 1. If it's already parented to a bone (Mixamo naming), let it be
        const isAlreadyBoneParented = m.parent && m.parent.name.toLowerCase().includes("mixamorig");

        if (!isAlreadyBoneParented) {
          console.log("[DEBUG] Number mesh is not bone-parented, applying user coordinates...");
          if (spineBone && mainMesh) {
            m.attachToBone(spineBone, mainMesh);

            // Position on the back of the jersey - centered horizontally on back
            m.position.set(0, -0.20, -0.08);

            // Rotate to lie flat against the back
            // Y=180 flips it to face backward, X=90 tilts it to lie flat on the back
            m.rotationQuaternion = BABYLON.Quaternion.RotationYawPitchRoll(Math.PI, Math.PI / 2, 0);

            // Scale to fit the back properly
            m.scaling.set(0.5, 0.5, 0.5);

            console.log("[DEBUG] Attached number mesh flat on back of jersey.");
          }
        } else {
          console.log("[DEBUG] Number mesh already has bone parent:", m.parent?.name);
          // Force back placement and corrected orientation even if already parented
          m.position.set(0, -0.20, -0.08);
          m.rotationQuaternion = BABYLON.Quaternion.RotationYawPitchRoll(Math.PI, Math.PI / 2, 0);
          m.scaling.set(0.5, 0.5, 0.5);
        }

        // Boost material visibility
        if (m instanceof BABYLON.Mesh && m.material instanceof BABYLON.PBRMaterial) {
          m.material.emissiveColor = new BABYLON.Color3(0.6, 0.6, 0.6);
          m.material.disableLighting = false;
        }
      }
    });
  }

  // 2. FALLBACK: If no number was found and this is the player, create a dynamic one
  if (!numberFound && name === "player") {
    console.log("[DEBUG] No number mesh found in model, creating dynamic fallback '7'");
    const dynamicNumber = createJerseyNumber("player", scene, "7", c.jersey);
    if (dynamicNumber) {
      attachToBack(dynamicNumber, 0.05, -0.14);
      // Boost visibility
      if (dynamicNumber.material instanceof BABYLON.PBRMaterial) {
        dynamicNumber.material.emissiveColor = new BABYLON.Color3(0.5, 0.5, 0.5);
      }
    }
  }

  // ADD LOGO to the chest
  const logo = createTeamLogo(name, scene, c.jersey);
  if (logo) {
    // Logo on chest: slightly above bone origin, forward (+Z in bone space usually)
    attachToBody(logo as BABYLON.Mesh, 0.15, 0.12, 0);
    logo.scaling.set(0.6, 0.6, 0.6);
  }

  // Shadow casting

  // Add shadow casting
  if (shadowGenerator) {
    for (const mesh of allMeshes) {
      if (mesh instanceof BABYLON.Mesh) {
        shadowGenerator.addShadowCaster(mesh);
      }
    }
  }

  // Get run animation group from instance
  let runAnim: BABYLON.AnimationGroup | null = null;
  if (runInstance.animationGroups.length > 0) {
    runAnim = runInstance.animationGroups[0];
    runAnim.stop();
    runAnim.loopAnimation = true;
  }

  // MODEL-SWAP approach: instantiate the celebrate container as a separate model.
  // When celebrating, we hide the run model and show the celebrate model (and vice versa).
  let celebrateAnim: BABYLON.AnimationGroup | null = null;
  const celebrateRootNodes: BABYLON.Node[] = [];
  const runRootNodes: BABYLON.Node[] = [...runInstance.rootNodes];
  try {
    const celebrateInstance = celebrateContainer.instantiateModelsToScene(
      (sourceName) => `${name}_celebrate_${sourceName}`
    );

    // Parent celebrate meshes under the same root so they follow the player.
    // Match the run model's root node transform so both models align.
    const runRootNode = runInstance.rootNodes[0];
    for (const node of celebrateInstance.rootNodes) {
      node.parent = root;
      // Copy position/rotation/scaling from the run model's root so they overlap
      if (runRootNode && node instanceof BABYLON.TransformNode && runRootNode instanceof BABYLON.TransformNode) {
        node.position.copyFrom(runRootNode.position);
        node.rotation.copyFrom(runRootNode.rotation);
        node.scaling.copyFrom(runRootNode.scaling);
      }
      celebrateRootNodes.push(node);

      // Copy materials from run model to celebrate model so they look identical.
      // The run model (actualmodel.glb from Blender) has 3 primitives: SkinMat, JerseyMat, PantsMat.
      // The celebrate model (excited.glb from Mixamo) has 2 primitives: Ch12_body, Ch12_hair.
      // Since the celebrate body mesh covers skin+clothes in one mesh, apply the jersey
      // material (the most recognizable player color) to the body, and skin to the hair.
      const celebChildMeshes = node.getChildMeshes();
      const jerseyMesh = allMeshes.find(m => m.material?.name.toLowerCase().includes("jersey"));
      const skinMesh = allMeshes.find(m => m.material?.name.toLowerCase().includes("skin"));
      for (const celebMesh of celebChildMeshes) {
        const mName = celebMesh.name.toLowerCase();
        if (mName.includes("primitive0") || mName.includes("body")) {
          if (jerseyMesh?.material) celebMesh.material = jerseyMesh.material;
        } else if (mName.includes("primitive1") || mName.includes("hair")) {
          if (skinMesh?.material) celebMesh.material = skinMesh.material;
        }
      }

      // Start hidden — only shown when celebrating
      // Use setEnabled(false) on root; this cascades to all descendants via isEnabled() checks
      node.setEnabled(false);
    }

    if (celebrateInstance.animationGroups.length > 0) {
      celebrateAnim = celebrateInstance.animationGroups[0];
      celebrateAnim.stop();
      celebrateAnim.loopAnimation = true;
    }
    console.log(`[DEBUG] Celebrate model loaded with ${celebrateRootNodes.length} root nodes, anim: ${!!celebrateAnim}`);
  } catch (e) {
    console.warn("Could not load celebrate animation:", e);
  }

  const animations: FBXAnimations = {
    idle: null,
    walk: null,
    run: runAnim,
    celebrate: celebrateAnim,
    runRootNodes,
    celebrateRootNodes,
  };

  // ROOT MOTION KILLER:
  // Mixamo and other animations often have 'root motion' where the character moves
  // logical distance in the animation. We must neutralize this so our logical
  // movement system in Player.ts has full control.
  const animationsToClean = [runAnim, celebrateAnim];
  for (const group of animationsToClean) {
    if (!group) continue;
    for (const targetedAnim of group.targetedAnimations) {
      const animation = targetedAnim.animation;
      const target = targetedAnim.target as any;

      if (animation.targetProperty === "position" || animation.targetProperty === "translation") {
        const tName = target.name || "";
        if (tName.toLowerCase().includes("root") || tName.toLowerCase().includes("hips")) {
          const keys = animation.getKeys();
          for (const key of keys) {
            key.value.x = 0;
            key.value.z = 0;
          }
        }
      }
    }
  }

  // Start the run animation immediately to prevent T-pose
  if (runAnim) {
    runAnim.play(true);
    runAnim.goToFrame(0);
    runAnim.stop();
  }

  console.log(`FBX character '${name}' loaded with mesh + animations!`);
  return { root, animations };
}

/**
 * Create a fully procedural goalkeeper character — completely different model
 * from the player's GLB. Built from BabylonJS primitives with GK-specific
 * gear: long-sleeve jersey, long pants, padded gloves, and a GK cap.
 */
export function createGoalkeeperMesh(
  name: string,
  scene: BABYLON.Scene,
  jerseyColor: BABYLON.Color3,
  shadowGenerator?: BABYLON.ShadowGenerator
): { root: BABYLON.Mesh; limbs: HumanoidLimbs } {
  const shadowParts: BABYLON.Mesh[] = [];

  // ===== GK-SPECIFIC MATERIALS =====
  const gkJerseyMat = new BABYLON.PBRMaterial(`${name}_gk_jersey`, scene);
  gkJerseyMat.albedoColor = jerseyColor;
  gkJerseyMat.roughness = 0.55;
  gkJerseyMat.metallic = 0.05;
  gkJerseyMat.sheen.isEnabled = true;
  gkJerseyMat.sheen.intensity = 0.6;
  gkJerseyMat.backFaceCulling = false;

  const gkPantsMat = new BABYLON.PBRMaterial(`${name}_gk_pants`, scene);
  gkPantsMat.albedoColor = new BABYLON.Color3(0.08, 0.08, 0.08);
  gkPantsMat.roughness = 0.6;

  const gkSkinMat = createSkinMaterial(`${name}_gk_skin`, new BABYLON.Color3(0.85, 0.72, 0.6), scene);

  const gkGloveMat = new BABYLON.PBRMaterial(`${name}_gk_gloves`, scene);
  gkGloveMat.albedoColor = new BABYLON.Color3(0.0, 0.95, 0.3);
  gkGloveMat.roughness = 0.85;

  const gkBootsMat = createBootsMaterial(`${name}_gk_boots`, new BABYLON.Color3(0.1, 0.1, 0.1), scene);

  const gkCapMat = new BABYLON.PBRMaterial(`${name}_gk_cap`, scene);
  gkCapMat.albedoColor = jerseyColor;
  gkCapMat.roughness = 0.7;

  const hairMat = createHairMaterial(`${name}_gk_hair`, new BABYLON.Color3(0.25, 0.18, 0.1), scene);
  const eyeWhiteMat = createEyeWhiteMaterial(scene);
  const eyeIrisMat = createEyeIrisMaterial(new BABYLON.Color3(0.3, 0.2, 0.1), scene);
  const lipMat = createLipMaterial(scene);

  // Root
  const root = BABYLON.MeshBuilder.CreateBox(name, { size: 0.01 }, scene);
  root.isVisible = false;

  let torsoMesh: BABYLON.Mesh;
  let headMesh: BABYLON.Mesh;

  // Body group — feet on ground
  const bodyGroup = new BABYLON.TransformNode(`${name}_bodyGroup`, scene);
  bodyGroup.parent = root;
  bodyGroup.position.y = 1.45;

  // ========== HEAD ==========
  const headGroup = new BABYLON.TransformNode(`${name}_headGroup`, scene);
  headGroup.parent = bodyGroup;
  headGroup.position.y = 0.52;

  const headResult = createRealisticHead(name, scene, gkSkinMat, hairMat, eyeWhiteMat, eyeIrisMat, lipMat, headGroup, shadowParts);
  headMesh = headResult.skull;

  // GK Cap / flat cap
  const capBrim = BABYLON.MeshBuilder.CreateDisc(`${name}_capBrim`, { radius: 0.16, tessellation: 20 }, scene);
  capBrim.position = new BABYLON.Vector3(0, 0.14, 0.10);
  capBrim.rotation.x = -Math.PI / 2 + 0.2;
  capBrim.material = gkCapMat;
  capBrim.parent = headGroup;
  shadowParts.push(capBrim);

  const capTop = BABYLON.MeshBuilder.CreateSphere(`${name}_capTop`, { diameter: 0.26, segments: 14 }, scene);
  capTop.scaling = new BABYLON.Vector3(1.1, 0.5, 1.1);
  capTop.position = new BABYLON.Vector3(0, 0.16, 0);
  capTop.material = gkCapMat;
  capTop.parent = headGroup;
  shadowParts.push(capTop);

  // Neck
  const neck = BABYLON.MeshBuilder.CreateCylinder(`${name}_neck`, { height: 0.18, diameterTop: 0.13, diameterBottom: 0.15, tessellation: 16 }, scene);
  neck.position = new BABYLON.Vector3(0, 0.27, 0);
  neck.material = gkSkinMat;
  neck.parent = bodyGroup;
  shadowParts.push(neck);

  // ========== TORSO — long-sleeve jersey ==========
  const chest = BABYLON.MeshBuilder.CreateCapsule(`${name}_chest`, { height: 0.50, radius: 0.21, tessellation: 20 }, scene);
  chest.scaling = new BABYLON.Vector3(1.1, 1.0, 0.85);
  chest.position = new BABYLON.Vector3(0, 0.12, 0);
  chest.material = gkJerseyMat;
  chest.parent = bodyGroup;
  shadowParts.push(chest);
  torsoMesh = chest;

  // Shoulders — padded GK shoulders
  for (const side of [-1, 1]) {
    const shoulder = BABYLON.MeshBuilder.CreateSphere(`${name}_shoulder_${side}`, { diameter: 0.20, segments: 14 }, scene);
    shoulder.position = new BABYLON.Vector3(side * 0.24, 0.24, 0.02);
    shoulder.material = gkJerseyMat;
    shoulder.parent = bodyGroup;
    shadowParts.push(shoulder);

    // Shoulder padding
    const pad = BABYLON.MeshBuilder.CreateSphere(`${name}_pad_${side}`, { diameter: 0.12, segments: 10 }, scene);
    pad.position = new BABYLON.Vector3(side * 0.27, 0.26, 0.02);
    pad.scaling = new BABYLON.Vector3(0.8, 1.0, 0.6);
    pad.material = gkJerseyMat;
    pad.parent = bodyGroup;
    shadowParts.push(pad);
  }

  // Collar — round crew neck
  const collar = BABYLON.MeshBuilder.CreateTorus(`${name}_collar`, { diameter: 0.18, thickness: 0.018, tessellation: 20 }, scene);
  collar.position = new BABYLON.Vector3(0, 0.28, 0);
  collar.rotation.x = Math.PI / 2;
  const collarMat = new BABYLON.PBRMaterial(`${name}_collarMat`, scene);
  collarMat.albedoColor = new BABYLON.Color3(0.95, 0.95, 0.95);
  collarMat.roughness = 0.5;
  collar.material = collarMat;
  collar.parent = bodyGroup;
  shadowParts.push(collar);

  // Abdomen
  const abdomen = BABYLON.MeshBuilder.CreateCapsule(`${name}_abdomen`, { height: 0.32, radius: 0.16, tessellation: 16 }, scene);
  abdomen.scaling = new BABYLON.Vector3(1.0, 1.0, 0.8);
  abdomen.position = new BABYLON.Vector3(0, -0.20, 0);
  abdomen.material = gkJerseyMat;
  abdomen.parent = bodyGroup;
  shadowParts.push(abdomen);

  // Hips — long pants start here
  const hips = BABYLON.MeshBuilder.CreateCapsule(`${name}_hips`, { height: 0.22, radius: 0.16, tessellation: 16 }, scene);
  hips.position = new BABYLON.Vector3(0, -0.42, 0);
  hips.material = gkPantsMat;
  hips.parent = bodyGroup;
  shadowParts.push(hips);

  // Jersey number "1" on back
  const numberMesh = createJerseyNumber(name, scene, "1", jerseyColor);
  if (numberMesh) {
    numberMesh.position = new BABYLON.Vector3(0, 0.05, -0.19);
    numberMesh.rotation.y = Math.PI;
    numberMesh.parent = bodyGroup;
    shadowParts.push(numberMesh);
  }

  // ========== ARMS — long sleeves + gloves ==========
  const armPivots: BABYLON.TransformNode[] = [];
  for (const side of [-1, 1]) {
    const armPivot = new BABYLON.TransformNode(`${name}_armPivot_${side}`, scene);
    armPivot.parent = bodyGroup;
    armPivot.position = new BABYLON.Vector3(side * 0.22, 0.18, 0);
    armPivots.push(armPivot);

    // Upper arm — long sleeve (jersey color, not skin)
    const upperArm = BABYLON.MeshBuilder.CreateCapsule(`${name}_upperArm_${side}`, { height: 0.32, radius: 0.055, tessellation: 14 }, scene);
    upperArm.position.y = -0.15;
    upperArm.material = gkJerseyMat;
    upperArm.parent = armPivot;
    shadowParts.push(upperArm);

    // Elbow pad
    const elbow = BABYLON.MeshBuilder.CreateSphere(`${name}_elbow_${side}`, { diameter: 0.085, segments: 10 }, scene);
    elbow.position.y = -0.30;
    elbow.material = gkJerseyMat;
    elbow.parent = armPivot;
    shadowParts.push(elbow);

    // Forearm — long sleeve
    const forearm = BABYLON.MeshBuilder.CreateCapsule(`${name}_forearm_${side}`, { height: 0.30, radius: 0.045, tessellation: 12 }, scene);
    forearm.position.y = -0.44;
    forearm.material = gkJerseyMat;
    forearm.parent = armPivot;
    shadowParts.push(forearm);

    // GK Glove — chunky padded glove (much bigger than regular hand)
    const glove = BABYLON.MeshBuilder.CreateBox(`${name}_glove_${side}`, { width: 0.10, height: 0.12, depth: 0.05 }, scene);
    glove.position.y = -0.62;
    glove.material = gkGloveMat;
    glove.parent = armPivot;
    shadowParts.push(glove);

    // Glove padding on back of hand
    const glovePad = BABYLON.MeshBuilder.CreateBox(`${name}_glovePad_${side}`, { width: 0.09, height: 0.08, depth: 0.025 }, scene);
    glovePad.position = new BABYLON.Vector3(0, -0.60, -0.03);
    glovePad.material = gkGloveMat;
    glovePad.parent = armPivot;
    shadowParts.push(glovePad);

    // Thick GK fingers
    for (let fi = 0; fi < 4; fi++) {
      const finger = BABYLON.MeshBuilder.CreateCylinder(`${name}_gkfinger_${side}_${fi}`, { height: 0.04, diameter: 0.018, tessellation: 8 }, scene);
      finger.position = new BABYLON.Vector3((fi - 1.5) * 0.022, -0.69, 0.01);
      finger.material = gkGloveMat;
      finger.parent = armPivot;
      shadowParts.push(finger);
    }

    // Thumb
    const thumb = BABYLON.MeshBuilder.CreateCylinder(`${name}_gkthumb_${side}`, { height: 0.03, diameter: 0.018, tessellation: 8 }, scene);
    thumb.position = new BABYLON.Vector3(side * 0.04, -0.64, -0.01);
    thumb.rotation.z = side * 0.6;
    thumb.material = gkGloveMat;
    thumb.parent = armPivot;
    shadowParts.push(thumb);

    // Wrist strap
    const wristStrap = BABYLON.MeshBuilder.CreateTorus(`${name}_wristStrap_${side}`, { diameter: 0.09, thickness: 0.012, tessellation: 14 }, scene);
    wristStrap.position.y = -0.56;
    wristStrap.rotation.x = Math.PI / 2;
    wristStrap.material = collarMat;
    wristStrap.parent = armPivot;
    shadowParts.push(wristStrap);
  }

  // ========== LEGS — long pants down to ankle ==========
  const legPivots: BABYLON.TransformNode[] = [];
  for (const side of [-1, 1]) {
    const legPivot = new BABYLON.TransformNode(`${name}_legPivot_${side}`, scene);
    legPivot.parent = bodyGroup;
    legPivot.position = new BABYLON.Vector3(side * 0.11, -0.52, 0);
    legPivots.push(legPivot);

    // Thigh — long pants
    const thigh = BABYLON.MeshBuilder.CreateCapsule(`${name}_thigh_${side}`, { height: 0.45, radius: 0.075, tessellation: 14 }, scene);
    thigh.position.y = -0.21;
    thigh.material = gkPantsMat;
    thigh.parent = legPivot;
    shadowParts.push(thigh);

    // Knee pad
    const knee = BABYLON.MeshBuilder.CreateSphere(`${name}_knee_${side}`, { diameter: 0.10, segments: 10 }, scene);
    knee.position.y = -0.42;
    knee.material = gkPantsMat;
    knee.parent = legPivot;
    shadowParts.push(knee);

    // Knee padding (extra padding for GK dives)
    const kneePad = BABYLON.MeshBuilder.CreateBox(`${name}_kneePad_${side}`, { width: 0.08, height: 0.06, depth: 0.04 }, scene);
    kneePad.position = new BABYLON.Vector3(0, -0.42, 0.04);
    kneePad.material = gkJerseyMat;
    kneePad.parent = legPivot;
    shadowParts.push(kneePad);

    // Shin — long pants
    const shin = BABYLON.MeshBuilder.CreateCapsule(`${name}_shin_${side}`, { height: 0.42, radius: 0.055, tessellation: 12 }, scene);
    shin.position.y = -0.62;
    shin.material = gkPantsMat;
    shin.parent = legPivot;
    shadowParts.push(shin);

    // Ankle
    const ankle = BABYLON.MeshBuilder.CreateSphere(`${name}_ankle_${side}`, { diameter: 0.065, segments: 8 }, scene);
    ankle.position.y = -0.82;
    ankle.material = gkPantsMat;
    ankle.parent = legPivot;
    shadowParts.push(ankle);

    // Boot
    const boot = BABYLON.MeshBuilder.CreateBox(`${name}_boot_${side}`, { width: 0.10, height: 0.10, depth: 0.26 }, scene);
    boot.position = new BABYLON.Vector3(0, -0.86, 0.02);
    boot.material = gkBootsMat;
    boot.parent = legPivot;
    shadowParts.push(boot);
  }

  root.position.y = 0;

  if (shadowGenerator) {
    shadowParts.forEach((p) => shadowGenerator.addShadowCaster(p));
  }

  return {
    root,
    limbs: {
      leftArm: armPivots[0],
      rightArm: armPivots[1],
      leftLeg: legPivots[0],
      rightLeg: legPivots[1],
      head: headMesh,
      torso: torsoMesh,
      torsoGroup: bodyGroup,
      headGroup: headGroup,
      leftEyelid: headResult.leftEyelid,
      rightEyelid: headResult.rightEyelid,
    },
  };
}

// Realistic human proportions - CONNECTED body
// Based on Cristiano Ronaldo proportions - muscular athletic build
const PROPORTIONS = {
  totalHeight: 1.87,  // Ronaldo height
  headHeight: 0.30,   // Proportional head
  headWidth: 0.22,    // Ronaldo facial structure
  headDepth: 0.26,
  neckHeight: 0.13,   // Thick muscular neck
  neckWidth: 0.12,
  torsoHeight: 0.60,
  shoulderWidth: 0.52, // Broad shoulders
  chestWidth: 0.42,    // Muscular chest
  waistWidth: 0.32,    // V-taper
  hipWidth: 0.35,
  upperLeg: 0.48,      // Muscular legs
  lowerLeg: 0.46,
  upperArm: 0.34,      // Muscular arms
  lowerArm: 0.30,
  handLength: 0.095,
  footLength: 0.28,
};

const DEFAULT_COLORS: HumanoidColors = {
  skin: new BABYLON.Color3(1.0, 0.95, 0.9), // Extremely pale skin tone
  jersey: new BABYLON.Color3(0.9, 0.05, 0.05), // Pure Red jersey
  shorts: new BABYLON.Color3(0.05, 0.7, 0.05), // Vibrant Green shorts
  boots: new BABYLON.Color3(0.95, 0.95, 0.95), // White boots
  hair: new BABYLON.Color3(0.08, 0.06, 0.05), // Very dark brown/black
  eyeColor: new BABYLON.Color3(0.15, 0.25, 0.40), // Blue-ish eyes
};

// ========== JERSEY NUMBER ==========

function createJerseyNumber(
  name: string,
  scene: BABYLON.Scene,
  number: string,
  jerseyColor: BABYLON.Color3
): BABYLON.Mesh | null {
  // Create dynamic texture for the number
  const textureSize = 256;
  const dynamicTexture = new BABYLON.DynamicTexture(
    `${name}_numberTexture`,
    { width: textureSize, height: textureSize },
    scene
  );

  // Draw the number
  const ctx = dynamicTexture.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, textureSize, textureSize);

  // Determine text color based on jersey color brightness
  const brightness = jerseyColor.r * 0.299 + jerseyColor.g * 0.587 + jerseyColor.b * 0.114;
  const textColor = brightness > 0.5 ? "#000000" : "#FFFFFF";

  // Draw number with bold font
  ctx.font = "bold 160px Arial, sans-serif";
  ctx.fillStyle = textColor;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(number, textureSize / 2, textureSize / 2);

  dynamicTexture.update();

  // Create material with the number texture
  const numberMat = new BABYLON.PBRMaterial(`${name}_numberMat`, scene);
  numberMat.albedoTexture = dynamicTexture;
  // BUMP MAPPING for sculpted effect
  numberMat.bumpTexture = dynamicTexture;
  numberMat.invertNormalMapX = true;
  numberMat.invertNormalMapY = true;

  numberMat.roughness = 0.8; // Rougher inside the carving
  numberMat.metallic = 0.0;
  numberMat.backFaceCulling = false;

  // Parallax mapping for extra depth perception
  numberMat.useParallax = true;
  numberMat.useParallaxOcclusion = true;
  numberMat.parallaxScaleBias = 0.05;

  // Create a plane for the number
  const numberPlane = BABYLON.MeshBuilder.CreatePlane(
    `${name}_jerseyNumber`,
    { width: 0.20, height: 0.20 },
    scene
  );
  numberPlane.material = numberMat;

  return numberPlane;
}

// ========== JERSEY NAME ==========

function createJerseyName(
  name: string,
  scene: BABYLON.Scene,
  playerName: string,
  jerseyColor: BABYLON.Color3
): BABYLON.Mesh | null {
  const textureWidth = 512;
  const textureHeight = 128;
  const dynamicTexture = new BABYLON.DynamicTexture(
    `${name}_nameTexture`,
    { width: textureWidth, height: textureHeight },
    scene
  );

  const ctx = dynamicTexture.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, textureWidth, textureHeight);

  const brightness = jerseyColor.r * 0.299 + jerseyColor.g * 0.587 + jerseyColor.b * 0.114;
  const textColor = brightness > 0.5 ? "#000000" : "#FFFFFF";

  ctx.font = "bold 80px Arial, sans-serif";
  ctx.fillStyle = textColor;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(playerName, textureWidth / 2, textureHeight / 2);

  dynamicTexture.update();

  const nameMat = new BABYLON.PBRMaterial(`${name}_nameMat`, scene);
  nameMat.albedoTexture = dynamicTexture;
  // Sculpted bump effect
  nameMat.bumpTexture = dynamicTexture;
  nameMat.invertNormalMapX = true;
  nameMat.invertNormalMapY = true;

  nameMat.roughness = 0.8;
  nameMat.metallic = 0.0;
  nameMat.backFaceCulling = false;

  nameMat.useParallax = true;
  nameMat.useParallaxOcclusion = true;
  nameMat.parallaxScaleBias = 0.05;

  const namePlane = BABYLON.MeshBuilder.CreatePlane(
    `${name}_jerseyName`,
    { width: 0.28, height: 0.07 },
    scene
  );
  namePlane.material = nameMat;

  return namePlane;
}

// ========== TEAM LOGO ==========

function createTeamLogo(
  name: string,
  scene: BABYLON.Scene,
  jerseyColor: BABYLON.Color3
): BABYLON.Mesh | null {
  const textureSize = 256;
  const dynamicTexture = new BABYLON.DynamicTexture(
    `${name}_logoTexture`,
    { width: textureSize, height: textureSize },
    scene
  );

  const ctx = dynamicTexture.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, textureSize, textureSize);

  const brightness = jerseyColor.r * 0.299 + jerseyColor.g * 0.587 + jerseyColor.b * 0.114;
  const logoColor = brightness > 0.5 ? "#000000" : "#FFFFFF";
  const accentColor = brightness > 0.5 ? "#FFD700" : "#FFD700"; // Gold accent

  const centerX = textureSize / 2;
  const centerY = textureSize / 2;

  // Draw a custom geometric star logo (non-copyrighted)
  // Outer circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, 100, 0, 2 * Math.PI);
  ctx.strokeStyle = logoColor;
  ctx.lineWidth = 8;
  ctx.stroke();

  // Inner hexagon
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (i * 60 - 30) * Math.PI / 180;
    const x = centerX + 60 * Math.cos(angle);
    const y = centerY + 60 * Math.sin(angle);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = accentColor;
  ctx.fill();
  ctx.strokeStyle = logoColor;
  ctx.lineWidth = 4;
  ctx.stroke();

  // Star in center
  ctx.beginPath();
  const spikes = 5;
  const outerRadius = 40;
  const innerRadius = 20;
  for (let i = 0; i < spikes * 2; i++) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const angle = (i * 36 - 90) * Math.PI / 180;
    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = logoColor;
  ctx.fill();

  dynamicTexture.update();

  const logoMat = new BABYLON.PBRMaterial(`${name}_logoMat`, scene);
  logoMat.albedoTexture = dynamicTexture;
  logoMat.roughness = 0.4;
  logoMat.metallic = 0.0;
  logoMat.backFaceCulling = false;
  logoMat.alpha = 0.95;

  const logoPlane = BABYLON.MeshBuilder.CreatePlane(
    `${name}_teamLogo`,
    { width: 0.10, height: 0.10 },
    scene
  );
  logoPlane.material = logoMat;

  return logoPlane;
}

// ========== COLLAR MATERIAL ==========

function createCollarMaterial(scene: BABYLON.Scene, jerseyColor: BABYLON.Color3): BABYLON.PBRMaterial {
  const mat = new BABYLON.PBRMaterial("collarMat", scene);
  // Slightly darker than jersey
  mat.albedoColor = new BABYLON.Color3(
    Math.max(0, jerseyColor.r - 0.1),
    Math.max(0, jerseyColor.g - 0.1),
    Math.max(0, jerseyColor.b - 0.1)
  );
  mat.roughness = 0.8;
  return mat;
}

// ========== STRIPE MATERIAL ==========

function createStripeMaterial(scene: BABYLON.Scene, jerseyColor: BABYLON.Color3): BABYLON.PBRMaterial {
  const mat = new BABYLON.PBRMaterial("stripeMat", scene);
  // White accent for Manchester United style
  mat.albedoColor = new BABYLON.Color3(0.98, 0.98, 0.98); // White
  mat.roughness = 0.5;
  return mat;
}

// ========== CAPTAIN'S ARMBAND ==========

function createCaptainArmband(name: string, scene: BABYLON.Scene): BABYLON.Mesh {
  // Yellow captain's armband
  const armbandMat = new BABYLON.PBRMaterial(`${name}_armbandMat`, scene);
  armbandMat.albedoColor = new BABYLON.Color3(1.0, 0.9, 0.0); // Bright yellow
  armbandMat.roughness = 0.5;

  const armband = BABYLON.MeshBuilder.CreateCylinder(
    `${name}_captainArmband`,
    { height: 0.04, diameter: 0.09, tessellation: 16 },
    scene
  );
  armband.material = armbandMat;

  return armband;
}

export function createHumanoid(
  name: string,
  scene: BABYLON.Scene,
  colors: Partial<HumanoidColors> = {},
  shadowGenerator?: BABYLON.ShadowGenerator
): HumanoidResult {
  const c = { ...DEFAULT_COLORS, ...colors };

  // Create materials
  const skinMat = createSkinMaterial(`${name}_skin`, c.skin, scene);
  const jerseyMat = createJerseyMaterial(`${name}_jersey`, c.jersey, scene);
  const shortsMat = createShortsMaterial(`${name}_shorts`, c.shorts, scene);
  const bootsMat = createBootsMaterial(`${name}_boots`, c.boots, scene);
  const hairMat = createHairMaterial(`${name}_hair`, c.hair, scene);
  const eyeWhiteMat = createEyeWhiteMaterial(scene);
  const eyeIrisMat = createEyeIrisMaterial(c.eyeColor, scene);
  const lipMat = createLipMaterial(scene);

  // Root - pivot at ground level (feet)
  const root = BABYLON.MeshBuilder.CreateBox(name, { size: 0.01 }, scene);
  root.isVisible = false;

  const shadowParts: BABYLON.Mesh[] = [];
  let torsoMesh: BABYLON.Mesh;
  let headMesh: BABYLON.Mesh;

  // ========== SINGLE BODY GROUP - all body parts connect here ==========
  const bodyGroup = new BABYLON.TransformNode(`${name}_bodyGroup`, scene);
  bodyGroup.parent = root;
  // Body positioned so feet touch ground (Adjusted for leg length + clearance)
  bodyGroup.position.y = 1.45;

  // ========== HEAD - at top ==========
  const headGroup = new BABYLON.TransformNode(`${name}_headGroup`, scene);
  headGroup.parent = bodyGroup;
  // Position head so its bottom touches the neck top
  // Head height is PROPORTIONS.headHeight (0.32), center is at half height
  headGroup.position.y = 0.52; // Properly connected to neck

  // Skull and eyelids
  const headResult = createRealisticHead(name, scene, skinMat, hairMat, eyeWhiteMat, eyeIrisMat, lipMat, headGroup, shadowParts);
  headMesh = headResult.skull;
  const leftEyelidMesh = headResult.leftEyelid;
  const rightEyelidMesh = headResult.rightEyelid;

  // Neck - connects head to torso - properly sized for larger head
  const neckHeight = 0.18;
  const neck = BABYLON.MeshBuilder.CreateCylinder(
    `${name}_neck`,
    { height: neckHeight, diameterTop: 0.13, diameterBottom: 0.15, tessellation: 16 },
    scene
  );
  // Position neck so top touches bottom of head
  // Neck extends from (0.52 - 0.32/2 - neckHeight) to (0.52 - 0.32/2)
  // Head bottom is at 0.52 - 0.16 = 0.36
  // Neck top should be at 0.36, so center is at 0.36 - neckHeight/2 = 0.36 - 0.09 = 0.27
  neck.position = new BABYLON.Vector3(0, 0.27, 0);
  neck.material = skinMat;
  neck.parent = bodyGroup;
  shadowParts.push(neck);

  // Neck muscles (trapezius) - connects neck to shoulders
  for (const side of [-1, 1]) {
    const trap = BABYLON.MeshBuilder.CreateSphere(
      `${name}_trap_${side}`,
      { diameter: 0.10, segments: 12 },
      scene
    );
    trap.position = new BABYLON.Vector3(side * 0.07, 0.28, -0.01);
    trap.scaling = new BABYLON.Vector3(0.9, 1.3, 0.7);
    trap.material = skinMat;
    trap.parent = bodyGroup;
    shadowParts.push(trap);
  }

  // ========== TORSO - chest area ==========
  // Chest - Ronaldo's muscular build - smoother capsule
  const chestBase = BABYLON.MeshBuilder.CreateCapsule(
    `${name}_chest_base`,
    { height: 0.45, radius: 0.20, tessellation: 24 },
    scene
  );
  chestBase.scaling = new BABYLON.Vector3(1.15, 1.0, 0.85); // V-taper
  chestBase.position = new BABYLON.Vector3(0, 0.12, 0);
  chestBase.isVisible = false;

  // SCULPTING OPERATION: Create a recessed area on the back and front
  // This physically carves into the model's geometry
  const nameRecess = BABYLON.MeshBuilder.CreateBox("nameRecess", { width: 0.32, height: 0.12, depth: 0.04 }, scene);
  nameRecess.position = new BABYLON.Vector3(0, 0.24, -0.16);
  nameRecess.rotation.x = -0.15; // Match torso curve

  const numberRecess = BABYLON.MeshBuilder.CreateBox("numberRecess", { width: 0.25, height: 0.25, depth: 0.04 }, scene);
  numberRecess.position = new BABYLON.Vector3(0, 0.05, -0.17);

  // Logo recess on front
  const logoRecess = BABYLON.MeshBuilder.CreateBox("logoRecess", { width: 0.12, height: 0.12, depth: 0.04 }, scene);
  logoRecess.position = new BABYLON.Vector3(0, 0.18, 0.17);

  // Perform CSG subtraction
  const chestCSG = BABYLON.CSG.FromMesh(chestBase as BABYLON.Mesh);
  chestCSG.subtractInPlace(BABYLON.CSG.FromMesh(nameRecess));
  chestCSG.subtractInPlace(BABYLON.CSG.FromMesh(numberRecess));
  chestCSG.subtractInPlace(BABYLON.CSG.FromMesh(logoRecess));

  const chest = chestCSG.toMesh(`${name}_chest`, jerseyMat, scene);
  chest.parent = bodyGroup;

  // Refine geometry for crisp carved edges
  chest.forceSharedVertices();
  BABYLON.VertexData.ComputeNormals(
    chest.getVerticesData(BABYLON.VertexBuffer.PositionKind)!,
    chest.getIndices()!,
    chest.getVerticesData(BABYLON.VertexBuffer.NormalKind)!
  );

  // Ensure the interior of the carved holes is visible
  if (chest.material instanceof BABYLON.PBRMaterial) {
    chest.material.backFaceCulling = false;
  }

  // Cleanup CSG temporary meshes
  chestBase.dispose();
  nameRecess.dispose();
  numberRecess.dispose();
  logoRecess.dispose();

  shadowParts.push(chest);
  torsoMesh = chest;

  // Pectoral muscles definition
  for (const side of [-1, 1]) {
    const pec = BABYLON.MeshBuilder.CreateSphere(
      `${name}_pec_${side}`,
      { diameter: 0.14, segments: 12 },
      scene
    );
    pec.position = new BABYLON.Vector3(side * 0.12, 0.18, 0.18);
    pec.scaling = new BABYLON.Vector3(1.1, 0.9, 0.6);
    pec.material = jerseyMat;
    pec.parent = bodyGroup;
    shadowParts.push(pec);
  }

  // Jersey Collar - White trim like Manchester United
  const whiteMat = new BABYLON.PBRMaterial(`${name}_whiteTrim`, scene);
  whiteMat.albedoColor = new BABYLON.Color3(0.98, 0.98, 0.98);
  whiteMat.roughness = 0.5;

  const collarBack = BABYLON.MeshBuilder.CreateTorus(
    `${name}_collarBack`,
    { diameter: 0.18, thickness: 0.015, tessellation: 20 },
    scene
  );
  collarBack.position = new BABYLON.Vector3(0, 0.28, 0);
  collarBack.rotation.x = Math.PI / 2;
  collarBack.material = whiteMat;
  collarBack.parent = bodyGroup;
  shadowParts.push(collarBack);

  // V-neck front - white trim
  const vNeck = BABYLON.MeshBuilder.CreateBox(
    `${name}_vNeck`,
    { width: 0.06, height: 0.04, depth: 0.02 },
    scene
  );
  vNeck.position = new BABYLON.Vector3(0, 0.27, 0.16);
  vNeck.rotation.x = -0.3;
  vNeck.material = whiteMat;
  vNeck.parent = bodyGroup;
  shadowParts.push(vNeck);

  // Create white stripe material
  const stripeMat = createStripeMaterial(scene, c.jersey);

  // Shoulders - Ronaldo's broad muscular shoulders
  for (const side of [-1, 1]) {
    const shoulder = BABYLON.MeshBuilder.CreateSphere(
      `${name}_shoulder_${side}`,
      { diameter: 0.18, segments: 16 },
      scene
    );
    shoulder.position = new BABYLON.Vector3(side * 0.24, 0.26, 0.02);
    shoulder.material = jerseyMat;
    shoulder.parent = bodyGroup;
    shadowParts.push(shoulder);

    // Deltoid cap - prominent
    const deltoid = BABYLON.MeshBuilder.CreateSphere(
      `${name}_deltoid_${side}`,
      { diameter: 0.14, segments: 14 },
      scene
    );
    deltoid.position = new BABYLON.Vector3(side * 0.245, 0.22, 0.04);
    deltoid.scaling = new BABYLON.Vector3(0.9, 1.1, 1.0);
    deltoid.material = jerseyMat;
    deltoid.parent = bodyGroup;
    shadowParts.push(deltoid);

    // Sleeve stripes - custom design element
    const sleeveStripe = BABYLON.MeshBuilder.CreateTorus(
      `${name}_sleeveStripe_${side}`,
      { diameter: 0.13, thickness: 0.008, tessellation: 16 },
      scene
    );
    sleeveStripe.position = new BABYLON.Vector3(side * 0.21, 0.18, 0.02);
    sleeveStripe.rotation.z = side * Math.PI / 2;
    sleeveStripe.material = stripeMat;
    sleeveStripe.parent = bodyGroup;
    shadowParts.push(sleeveStripe);
  }

  // Side stripes on jersey
  for (const side of [-1, 1]) {
    const sideStripe = BABYLON.MeshBuilder.CreateBox(
      `${name}_sideStripe_${side}`,
      { width: 0.025, height: 0.35, depth: 0.02 },
      scene
    );
    sideStripe.position = new BABYLON.Vector3(side * 0.19, 0.08, 0);
    sideStripe.material = stripeMat;
    sideStripe.parent = bodyGroup;
    shadowParts.push(sideStripe);
  }

  // Custom team logo on chest (non-copyrighted geometric design)
  const logoMesh = createTeamLogo(name, scene, c.jersey);
  if (logoMesh) {
    logoMesh.position = new BABYLON.Vector3(0, 0.18, 0.155);
    logoMesh.parent = bodyGroup;

    // Apply sculpted material to logo
    if (logoMesh.material instanceof BABYLON.PBRMaterial) {
      logoMesh.material.bumpTexture = logoMesh.material.albedoTexture;
      logoMesh.material.invertNormalMapX = true;
      logoMesh.material.invertNormalMapY = true;
      logoMesh.material.useParallax = true;
      logoMesh.material.useParallaxOcclusion = true;
      logoMesh.material.parallaxScaleBias = 0.05;
      logoMesh.material.roughness = 0.8;
    }

    shadowParts.push(logoMesh);
  }

  // Abdomen - tapered athletic waist - smoother capsule
  const abdomen = BABYLON.MeshBuilder.CreateCapsule(
    `${name}_abdomen`,
    { height: 0.32, radius: 0.15, tessellation: 18 },
    scene
  );
  abdomen.scaling = new BABYLON.Vector3(1.0, 1.0, 0.8);
  abdomen.position = new BABYLON.Vector3(0, -0.20, 0);
  abdomen.material = jerseyMat;
  abdomen.parent = bodyGroup;
  shadowParts.push(abdomen);

  // Waist/hips - athletic build - smoother capsule
  const hips = BABYLON.MeshBuilder.CreateCapsule(
    `${name}_hips`,
    { height: 0.22, radius: 0.16, tessellation: 18 },
    scene
  );
  hips.position = new BABYLON.Vector3(0, -0.42, 0);
  hips.material = shortsMat;
  hips.parent = bodyGroup;
  shadowParts.push(hips);

  // Jersey number on back - sunken into the sculpted recess
  const numberMesh = createJerseyNumber(name, scene, "23", jerseyMat.albedoColor);
  if (numberMesh) {
    // Positioned slightly inside the CSG hole (z = -0.17 base, recessed by 0.04)
    numberMesh.position = new BABYLON.Vector3(0, 0.05, -0.155);
    numberMesh.rotation.y = Math.PI;
    numberMesh.parent = bodyGroup;
    shadowParts.push(numberMesh);
  }

  // Player name on back - sunken into the sculpted recess
  const nameMesh = createJerseyName(name, scene, "STRIKER", jerseyMat.albedoColor);
  if (nameMesh) {
    // Higher up in the name hole
    nameMesh.position = new BABYLON.Vector3(0, 0.24, -0.145);
    nameMesh.rotation.x = -0.15; // Angle to match back curve
    nameMesh.rotation.y = Math.PI;
    nameMesh.parent = bodyGroup;
    shadowParts.push(nameMesh);
  }

  // ========== ARMS - connect at shoulders ==========
  const armPivots: BABYLON.TransformNode[] = [];
  for (const side of [-1, 1]) {
    const armPivot = new BABYLON.TransformNode(`${name}_armPivot_${side}`, scene);
    armPivot.parent = bodyGroup;
    armPivot.position = new BABYLON.Vector3(side * 0.22, 0.18, 0);
    armPivots.push(armPivot);

    // Upper arm - smoother capsule
    const upperArm = BABYLON.MeshBuilder.CreateCapsule(
      `${name}_upperArm_${side}`,
      { height: 0.32, radius: 0.05, tessellation: 14 },
      scene
    );
    upperArm.position.y = -0.15;
    upperArm.material = skinMat;
    upperArm.parent = armPivot;
    shadowParts.push(upperArm);

    // Elbow
    const elbow = BABYLON.MeshBuilder.CreateSphere(
      `${name}_elbow_${side}`,
      { diameter: 0.075, segments: 12 },
      scene
    );
    elbow.position.y = -0.30;
    elbow.material = skinMat;
    elbow.parent = armPivot;
    shadowParts.push(elbow);

    // Forearm - smoother capsule
    const forearm = BABYLON.MeshBuilder.CreateCapsule(
      `${name}_forearm_${side}`,
      { height: 0.30, radius: 0.04, tessellation: 14 },
      scene
    );
    forearm.position.y = -0.30 - 0.14;
    forearm.material = skinMat;
    forearm.parent = armPivot;
    shadowParts.push(forearm);

    // Captain's armband on left arm (side = -1)
    if (side === -1) {
      const armband = createCaptainArmband(name, scene);
      armband.position.y = -0.28 - 0.05;
      armband.rotation.z = Math.PI / 2;
      armband.parent = armPivot;
      shadowParts.push(armband);
    }

    // Hand with fingers
    const hand = BABYLON.MeshBuilder.CreateBox(
      `${name}_hand_${side}`,
      { width: 0.06, height: 0.09, depth: 0.025 },
      scene
    );
    hand.position.y = -0.28 - 0.26 - 0.045;
    hand.material = skinMat;
    hand.parent = armPivot;
    shadowParts.push(hand);

    // Add fingers for more realistic hands
    const fingerPositions = [
      { x: -0.02, length: 0.035 },  // pinky
      { x: -0.007, length: 0.04 },  // ring
      { x: 0.007, length: 0.042 },  // middle
      { x: 0.02, length: 0.038 },   // index
    ];
    for (let fi = 0; fi < fingerPositions.length; fi++) {
      const finger = BABYLON.MeshBuilder.CreateCylinder(
        `${name}_finger_${side}_${fi}`,
        { height: fingerPositions[fi].length, diameter: 0.012, tessellation: 8 },
        scene
      );
      finger.position = new BABYLON.Vector3(
        fingerPositions[fi].x,
        -0.28 - 0.26 - 0.045 - 0.05,
        0.008
      );
      finger.material = skinMat;
      finger.parent = armPivot;
      shadowParts.push(finger);
    }

    // Thumb
    const thumb = BABYLON.MeshBuilder.CreateCylinder(
      `${name}_thumb_${side}`,
      { height: 0.025, diameter: 0.014, tessellation: 8 },
      scene
    );
    thumb.position = new BABYLON.Vector3(
      0.03,
      -0.28 - 0.26 - 0.06,
      -0.005
    );
    thumb.rotation.z = 0.5;
    thumb.material = skinMat;
    thumb.parent = armPivot;
    shadowParts.push(thumb);
  }

  // ========== LEGS - connect at hips ==========
  const legPivots: BABYLON.TransformNode[] = [];
  for (const side of [-1, 1]) {
    const legPivot = new BABYLON.TransformNode(`${name}_legPivot_${side}`, scene);
    legPivot.parent = bodyGroup;
    legPivot.position = new BABYLON.Vector3(side * 0.11, -0.52, 0);
    legPivots.push(legPivot);

    // Thigh - smoother capsule
    const thigh = BABYLON.MeshBuilder.CreateCapsule(
      `${name}_thigh_${side}`,
      { height: 0.45, radius: 0.07, tessellation: 16 },
      scene
    );
    thigh.position.y = -0.21;
    thigh.material = skinMat;
    thigh.parent = legPivot;
    shadowParts.push(thigh);

    // Knee
    const knee = BABYLON.MeshBuilder.CreateSphere(
      `${name}_knee_${side}`,
      { diameter: 0.09, segments: 10 },
      scene
    );
    knee.position.y = -0.42;
    knee.material = skinMat;
    knee.parent = legPivot;
    shadowParts.push(knee);

    // Shin - smoother capsule
    const shin = BABYLON.MeshBuilder.CreateCapsule(
      `${name}_shin_${side}`,
      { height: 0.42, radius: 0.05, tessellation: 14 },
      scene
    );
    shin.position.y = -0.42 - 0.20;
    shin.material = skinMat;
    shin.parent = legPivot;
    shadowParts.push(shin);

    // Ankle
    const ankle = BABYLON.MeshBuilder.CreateSphere(
      `${name}_ankle_${side}`,
      { diameter: 0.06, segments: 8 },
      scene
    );
    ankle.position.y = -0.42 - 0.40;
    ankle.material = skinMat;
    ankle.parent = legPivot;
    shadowParts.push(ankle);

    // Boot with toes
    const boot = BABYLON.MeshBuilder.CreateBox(
      `${name}_boot_${side}`,
      { width: 0.10, height: 0.10, depth: 0.26 },
      scene
    );
    boot.position = new BABYLON.Vector3(0, -0.42 - 0.40 - 0.02, 0.02);
    boot.material = bootsMat;
    boot.parent = legPivot;
    shadowParts.push(boot);

    // Add toes for more realistic feet
    const toePositions = [
      { x: -0.035, length: 0.025 },  // pinky toe
      { x: -0.012, length: 0.028 },   // ring toe
      { x: 0.008, length: 0.03 },     // middle toe
      { x: 0.028, length: 0.028 },   // big toe
    ];
    for (let ti = 0; ti < toePositions.length; ti++) {
      const toe = BABYLON.MeshBuilder.CreateSphere(
        `${name}_toe_${side}_${ti}`,
        { diameter: 0.018, segments: 8 },
        scene
      );
      toe.scaling = new BABYLON.Vector3(1, 0.7, 1.3);
      toe.position = new BABYLON.Vector3(
        toePositions[ti].x,
        -0.42 - 0.40 - 0.08,
        0.14
      );
      toe.material = skinMat;
      toe.parent = legPivot;
      shadowParts.push(toe);
    }

    // Studs
    for (let i = 0; i < 3; i++) {
      const stud = BABYLON.MeshBuilder.CreateCylinder(
        `${name}_stud_${side}_${i}`,
        { height: 0.015, diameter: 0.012, tessellation: 6 },
        scene
      );
      stud.position = new BABYLON.Vector3((i - 1) * 0.03, -0.42 - 0.40 - 0.08, 0.09);
      const studMat = new BABYLON.PBRMaterial(`${name}_studMat_${side}_${i}`, scene);
      studMat.albedoColor = new BABYLON.Color3(0.9, 0.9, 0.9);
      studMat.roughness = 0.2;
      studMat.metallic = 0.8; // Metallic studs
      stud.material = studMat;
      stud.parent = legPivot;
    }
  }

  // Adjust root to be at foot level
  root.position.y = 0;

  if (shadowGenerator) {
    shadowParts.forEach((p) => shadowGenerator.addShadowCaster(p));
  }

  return {
    root,
    limbs: {
      leftArm: armPivots[0],
      rightArm: armPivots[1],
      leftLeg: legPivots[0],
      rightLeg: legPivots[1],
      head: headMesh,
      torso: torsoMesh,
      torsoGroup: bodyGroup,
      headGroup: headGroup,
      leftEyelid: leftEyelidMesh,
      rightEyelid: rightEyelidMesh,
    },
  };
}

/**
 * Create a realistic human head with facial features
 */
interface HeadResult {
  skull: BABYLON.Mesh;
  leftEyelid: BABYLON.Mesh;
  rightEyelid: BABYLON.Mesh;
}

function createRealisticHead(
  name: string,
  scene: BABYLON.Scene,
  skinMat: BABYLON.PBRMaterial,
  hairMat: BABYLON.PBRMaterial,
  eyeWhiteMat: BABYLON.PBRMaterial,
  eyeIrisMat: BABYLON.PBRMaterial,
  lipMat: BABYLON.PBRMaterial,
  parent: BABYLON.TransformNode,
  shadowParts: BABYLON.Mesh[]
): HeadResult {
  const headGroup = new BABYLON.TransformNode(`${name}_realHead`, scene);
  headGroup.parent = parent;
  headGroup.position.y = PROPORTIONS.headHeight / 2;

  // Main skull - more natural egg shape with better proportions
  const skull = BABYLON.MeshBuilder.CreateSphere(
    `${name}_skull`,
    { diameter: PROPORTIONS.headHeight, segments: 32 },
    scene
  );
  // More realistic head proportions - slightly wider, less spherical
  skull.scaling = new BABYLON.Vector3(
    PROPORTIONS.headWidth / PROPORTIONS.headHeight * 1.15,
    1.05,
    PROPORTIONS.headDepth / PROPORTIONS.headHeight * 1.1
  );
  skull.position.y = 0.01;
  skull.material = skinMat;
  skull.parent = headGroup;
  shadowParts.push(skull);

  // Face plane - creates more defined facial structure
  const facePlane = BABYLON.MeshBuilder.CreateSphere(
    `${name}_facePlane`,
    { diameter: PROPORTIONS.headHeight * 0.88, segments: 24 },
    scene
  );
  facePlane.scaling = new BABYLON.Vector3(0.95, 0.92, 0.55);
  facePlane.position = new BABYLON.Vector3(0, -0.01, 0.065);
  facePlane.material = skinMat;
  facePlane.parent = headGroup;
  shadowParts.push(facePlane);

  // Forehead
  const forehead = BABYLON.MeshBuilder.CreateSphere(
    `${name}_forehead`,
    { diameter: 0.14, segments: 16 },
    scene
  );
  forehead.scaling = new BABYLON.Vector3(1.1, 0.4, 0.6);
  forehead.position = new BABYLON.Vector3(0, 0.06, 0.075);
  forehead.material = skinMat;
  forehead.parent = headGroup;
  shadowParts.push(forehead);

  // Brow ridge - more prominent for masculine look
  const browRidge = BABYLON.MeshBuilder.CreateBox(
    `${name}_brow`,
    { width: 0.13, height: 0.025, depth: 0.035 },
    scene
  );
  browRidge.position = new BABYLON.Vector3(0, 0.045, 0.095);
  browRidge.rotation.x = 0.15;
  browRidge.material = skinMat;
  browRidge.parent = headGroup;
  shadowParts.push(browRidge);

  // Cheekbones - more defined
  for (const side of [-1, 1]) {
    const cheek = BABYLON.MeshBuilder.CreateSphere(
      `${name}_cheek_${side}`,
      { diameter: 0.055, segments: 12 },
      scene
    );
    cheek.position = new BABYLON.Vector3(side * 0.065, -0.015, 0.085);
    cheek.scaling = new BABYLON.Vector3(0.8, 0.9, 0.6);
    cheek.material = skinMat;
    cheek.parent = headGroup;
    shadowParts.push(cheek);
  }

  // Jaw structure - more masculine defined jaw
  const jawLeft = BABYLON.MeshBuilder.CreateSphere(
    `${name}_jawLeft`,
    { diameter: 0.06, segments: 10 },
    scene
  );
  jawLeft.position = new BABYLON.Vector3(-0.055, -0.07, 0.065);
  jawLeft.scaling = new BABYLON.Vector3(0.5, 1.2, 0.8);
  jawLeft.material = skinMat;
  jawLeft.parent = headGroup;
  shadowParts.push(jawLeft);

  const jawRight = BABYLON.MeshBuilder.CreateSphere(
    `${name}_jawRight`,
    { diameter: 0.06, segments: 10 },
    scene
  );
  jawRight.position = new BABYLON.Vector3(0.055, -0.07, 0.065);
  jawRight.scaling = new BABYLON.Vector3(0.5, 1.2, 0.8);
  jawRight.material = skinMat;
  jawRight.parent = headGroup;
  shadowParts.push(jawRight);

  // Chin - stronger, more defined
  const chin = BABYLON.MeshBuilder.CreateSphere(
    `${name}_chin`,
    { diameter: 0.065, segments: 14 },
    scene
  );
  chin.scaling = new BABYLON.Vector3(1, 0.85, 0.8);
  chin.position = new BABYLON.Vector3(0, -0.095, 0.095);
  chin.material = skinMat;
  chin.parent = headGroup;
  shadowParts.push(chin);

  // ========== EYES ==========
  let leftEyelid: BABYLON.Mesh;
  let rightEyelid: BABYLON.Mesh;
  for (const side of [-1, 1]) {
    const eyeGroup = new BABYLON.TransformNode(`${name}_eyeGroup_${side}`, scene);
    eyeGroup.parent = headGroup;
    // Better eye positioning - slightly wider and higher
    eyeGroup.position = new BABYLON.Vector3(side * 0.042, 0.025, 0.088);

    // Eye socket - deeper set eyes
    const socket = BABYLON.MeshBuilder.CreateSphere(
      `${name}_socket_${side}`,
      { diameter: 0.038, segments: 14 },
      scene
    );
    socket.scaling = new BABYLON.Vector3(1.1, 0.85, 0.45);
    socket.position.z = -0.005;
    socket.material = skinMat;
    socket.parent = eyeGroup;
    shadowParts.push(socket);

    // Eye white - slightly larger
    const eyeWhite = BABYLON.MeshBuilder.CreateSphere(
      `${name}_eyeWhite_${side}`,
      { diameter: 0.027, segments: 14 },
      scene
    );
    eyeWhite.scaling = new BABYLON.Vector3(1, 0.95, 0.55);
    eyeWhite.position.z = 0.003;
    eyeWhite.material = eyeWhiteMat;
    eyeWhite.parent = eyeGroup;

    // Iris - properly sized
    const iris = BABYLON.MeshBuilder.CreateSphere(
      `${name}_iris_${side}`,
      { diameter: 0.016, segments: 12 },
      scene
    );
    iris.scaling = new BABYLON.Vector3(1, 1, 0.3);
    iris.position.z = 0.011;
    iris.material = eyeIrisMat;
    iris.parent = eyeGroup;

    // Pupil - black center
    const pupil = BABYLON.MeshBuilder.CreateSphere(
      `${name}_pupil_${side}`,
      { diameter: 0.008, segments: 10 },
      scene
    );
    pupil.scaling = new BABYLON.Vector3(1, 1, 0.4);
    pupil.position.z = 0.013;
    const pupilMat = new BABYLON.PBRMaterial(`${name}_pupilMat_${side}`, scene);
    pupilMat.albedoColor = new BABYLON.Color3(0.02, 0.02, 0.02);
    pupilMat.roughness = 0.0; // Very shiny
    pupilMat.metallic = 0.0;
    pupil.material = pupilMat;
    pupil.parent = eyeGroup;

    // Upper eyelid
    const eyelid = BABYLON.MeshBuilder.CreateSphere(
      `${name}_eyelid_${side}`,
      { diameter: 0.032, segments: 10 },
      scene
    );
    eyelid.scaling = new BABYLON.Vector3(1.1, 0.25, 0.6);
    eyelid.position.y = 0.012;
    eyelid.material = skinMat;
    eyelid.parent = eyeGroup;
    shadowParts.push(eyelid);

    // Lower eyelid
    const lowerEyelid = BABYLON.MeshBuilder.CreateSphere(
      `${name}_lowerEyelid_${side}`,
      { diameter: 0.028, segments: 8 },
      scene
    );
    lowerEyelid.scaling = new BABYLON.Vector3(1.1, 0.2, 0.5);
    lowerEyelid.position.y = -0.012;
    lowerEyelid.material = skinMat;
    lowerEyelid.parent = eyeGroup;
    shadowParts.push(lowerEyelid);

    // Store eyelid references for blinking animation
    if (side === -1) {
      leftEyelid = eyelid;
    } else {
      rightEyelid = eyelid;
    }
  }

  // ========== EYEBROWS ==========
  for (const side of [-1, 1]) {
    // Main brow
    const brow = BABYLON.MeshBuilder.CreateBox(
      `${name}_eyebrow_${side}`,
      { width: 0.038, height: 0.012, depth: 0.015 },
      scene
    );
    brow.position = new BABYLON.Vector3(side * 0.045, 0.055, 0.098);
    brow.rotation.z = side * 0.12;
    brow.rotation.x = 0.1;
    brow.material = hairMat;
    brow.parent = headGroup;
    shadowParts.push(brow);

    // Brow tail (thinner outer part)
    const browTail = BABYLON.MeshBuilder.CreateBox(
      `${name}_browTail_${side}`,
      { width: 0.02, height: 0.008, depth: 0.012 },
      scene
    );
    browTail.position = new BABYLON.Vector3(side * 0.068, 0.06, 0.095);
    browTail.rotation.z = side * 0.25;
    browTail.material = hairMat;
    browTail.parent = headGroup;
    shadowParts.push(browTail);
  }

  // ========== NOSE ==========
  // Nose bridge - longer and more defined
  const noseBridge = BABYLON.MeshBuilder.CreateCylinder(
    `${name}_noseBridge`,
    { height: 0.055, diameterTop: 0.012, diameterBottom: 0.022, tessellation: 10 },
    scene
  );
  noseBridge.position = new BABYLON.Vector3(0, -0.005, 0.105);
  noseBridge.rotation.x = 0.25;
  noseBridge.material = skinMat;
  noseBridge.parent = headGroup;
  shadowParts.push(noseBridge);

  // Nose tip - slightly larger for athletic look
  const noseTip = BABYLON.MeshBuilder.CreateSphere(
    `${name}_noseTip`,
    { diameter: 0.028, segments: 12 },
    scene
  );
  noseTip.scaling = new BABYLON.Vector3(1.1, 0.75, 0.85);
  noseTip.position = new BABYLON.Vector3(0, -0.032, 0.128);
  noseTip.material = skinMat;
  noseTip.parent = headGroup;
  shadowParts.push(noseTip);

  // Nostrils - more defined
  for (const side of [-1, 1]) {
    const nostril = BABYLON.MeshBuilder.CreateSphere(
      `${name}_nostril_${side}`,
      { diameter: 0.01, segments: 8 },
      scene
    );
    nostril.scaling = new BABYLON.Vector3(0.7, 0.4, 0.5);
    nostril.position = new BABYLON.Vector3(side * 0.01, -0.038, 0.118);
    nostril.material = skinMat;
    nostril.parent = headGroup;
    shadowParts.push(nostril);
  }

  // ========== MOUTH ==========
  // Upper lip - fuller
  const upperLip = BABYLON.MeshBuilder.CreateBox(
    `${name}_upperLip`,
    { width: 0.038, height: 0.008, depth: 0.015 },
    scene
  );
  upperLip.position = new BABYLON.Vector3(0, -0.062, 0.108);
  upperLip.rotation.x = 0.1;
  upperLip.material = lipMat;
  upperLip.parent = headGroup;
  shadowParts.push(upperLip);

  // Lower lip - fuller
  const lowerLip = BABYLON.MeshBuilder.CreateBox(
    `${name}_lowerLip`,
    { width: 0.034, height: 0.01, depth: 0.018 },
    scene
  );
  lowerLip.position = new BABYLON.Vector3(0, -0.075, 0.108);
  lowerLip.material = lipMat;
  lowerLip.parent = headGroup;
  shadowParts.push(lowerLip);

  // Philtrum (groove above upper lip)
  const philtrum = BABYLON.MeshBuilder.CreateBox(
    `${name}_philtrum`,
    { width: 0.008, height: 0.015, depth: 0.005 },
    scene
  );
  philtrum.position = new BABYLON.Vector3(0, -0.052, 0.105);
  philtrum.material = skinMat;
  philtrum.parent = headGroup;
  shadowParts.push(philtrum);

  // Mouth corners
  for (const side of [-1, 1]) {
    const corner = BABYLON.MeshBuilder.CreateSphere(
      `${name}_mouthCorner_${side}`,
      { diameter: 0.009, segments: 8 },
      scene
    );
    corner.position = new BABYLON.Vector3(side * 0.019, -0.068, 0.105);
    corner.material = lipMat;
    corner.parent = headGroup;
    shadowParts.push(corner);
  }

  // ========== EARS ==========
  for (const side of [-1, 1]) {
    // Main ear shape - more realistic proportions
    const ear = BABYLON.MeshBuilder.CreateSphere(
      `${name}_ear_${side}`,
      { diameter: 0.042, segments: 14 },
      scene
    );
    ear.scaling = new BABYLON.Vector3(0.35, 1.0, 0.5);
    ear.position = new BABYLON.Vector3(side * 0.092, 0.0, 0.0);
    ear.material = skinMat;
    ear.parent = headGroup;
    shadowParts.push(ear);

    // Ear helix (outer rim)
    const helix = BABYLON.MeshBuilder.CreateTorus(
      `${name}_earHelix_${side}`,
      { diameter: 0.038, thickness: 0.006, tessellation: 12 },
      scene
    );
    helix.scaling = new BABYLON.Vector3(0.9, 1.3, 0.6);
    helix.position = new BABYLON.Vector3(side * 0.098, 0.0, 0.002);
    helix.rotation.y = side * 0.3;
    helix.material = skinMat;
    helix.parent = headGroup;
    shadowParts.push(helix);

    // Ear lobe
    const lobe = BABYLON.MeshBuilder.CreateSphere(
      `${name}_earLobe_${side}`,
      { diameter: 0.018, segments: 8 },
      scene
    );
    lobe.scaling = new BABYLON.Vector3(0.8, 1.0, 0.7);
    lobe.position = new BABYLON.Vector3(side * 0.096, -0.018, 0.0);
    lobe.material = skinMat;
    lobe.parent = headGroup;
    shadowParts.push(lobe);
  }

  // ========== HAIR - Ronaldo Short Spiky Style ==========
  // Main hair - very short, close to head
  const hairVolume = BABYLON.MeshBuilder.CreateSphere(
    `${name}_hairVolume`,
    { diameter: PROPORTIONS.headHeight * 1.04, segments: 20 },
    scene
  );
  hairVolume.scaling = new BABYLON.Vector3(1.04, 0.5, 1.02); // Very flat on top
  hairVolume.position.y = 0.025;
  hairVolume.material = hairMat;
  hairVolume.parent = headGroup;
  shadowParts.push(hairVolume);

  // Spiky top pieces - Ronaldo's signature spiky hair
  for (let i = 0; i < 7; i++) {
    const spike = BABYLON.MeshBuilder.CreateBox(
      `${name}_hairSpike_${i}`,
      { width: 0.02, height: 0.04, depth: 0.02 },
      scene
    );
    const angle = (i / 7) * Math.PI * 2;
    spike.position = new BABYLON.Vector3(
      Math.cos(angle) * 0.06,
      0.08,
      Math.sin(angle) * 0.05 + 0.02
    );
    spike.rotation.x = (Math.random() - 0.5) * 0.3;
    spike.rotation.z = (Math.random() - 0.5) * 0.3;
    spike.material = hairMat;
    spike.parent = headGroup;
    shadowParts.push(spike);
  }

  // Short sides - very tight fade
  for (const side of [-1, 1]) {
    const hairSide = BABYLON.MeshBuilder.CreateSphere(
      `${name}_hairSide_${side}`,
      { diameter: 0.06, segments: 10 },
      scene
    );
    hairSide.scaling = new BABYLON.Vector3(0.3, 0.8, 0.9);
    hairSide.position = new BABYLON.Vector3(side * 0.10, 0.01, 0.0);
    hairSide.material = hairMat;
    hairSide.parent = headGroup;
    shadowParts.push(hairSide);

    // Short sideburns
    const sideburn = BABYLON.MeshBuilder.CreateBox(
      `${name}_sideburn_${side}`,
      { width: 0.012, height: 0.03, depth: 0.015 },
      scene
    );
    sideburn.position = new BABYLON.Vector3(side * 0.09, -0.04, 0.065);
    sideburn.material = hairMat;
    sideburn.parent = headGroup;
    shadowParts.push(sideburn);
  }

  // Hair back - short
  const hairBack = BABYLON.MeshBuilder.CreateSphere(
    `${name}_hairBack`,
    { diameter: 0.12, segments: 12 },
    scene
  );
  hairBack.scaling = new BABYLON.Vector3(1.02, 0.6, 0.4);
  hairBack.position = new BABYLON.Vector3(0, 0.0, -0.065);
  hairBack.material = hairMat;
  hairBack.parent = headGroup;
  shadowParts.push(hairBack);

  // Front hairline - short textured
  const hairFront = BABYLON.MeshBuilder.CreateSphere(
    `${name}_hairFront`,
    { diameter: 0.12, segments: 12 },
    scene
  );
  hairFront.scaling = new BABYLON.Vector3(1.05, 0.35, 0.5);
  hairFront.position = new BABYLON.Vector3(0, 0.04, 0.075);
  hairFront.material = hairMat;
  hairFront.parent = headGroup;
  shadowParts.push(hairFront);

  return { skull, leftEyelid: leftEyelid!, rightEyelid: rightEyelid! };
}

// ========== MATERIAL HELPERS ==========

function createSkinMaterial(name: string, color: BABYLON.Color3, scene: BABYLON.Scene): BABYLON.PBRMaterial {
  const mat = new BABYLON.PBRMaterial(name, scene);
  mat.albedoColor = color;
  mat.roughness = 0.45; // Human skin is somewhat rough
  mat.metallic = 0.0;
  mat.subSurface.isScatteringEnabled = true; // SSS effect for skin
  mat.subSurface.diffusionDistance = new BABYLON.Color3(0.05, 0.02, 0.02);
  return mat;
}

function createJerseyMaterial(name: string, color: BABYLON.Color3, scene: BABYLON.Scene): BABYLON.PBRMaterial {
  const mat = new BABYLON.PBRMaterial(name, scene);
  mat.albedoColor = color;
  mat.roughness = 0.7; // Fabric is rough
  mat.metallic = 0.0;
  mat.sheen.isEnabled = true; // Fabric sheen
  mat.sheen.intensity = 0.5;
  return mat;
}

function createShortsMaterial(name: string, color: BABYLON.Color3, scene: BABYLON.Scene): BABYLON.PBRMaterial {
  const mat = new BABYLON.PBRMaterial(name, scene);
  mat.albedoColor = color;
  mat.roughness = 0.6;
  mat.metallic = 0.0;
  return mat;
}

function createBootsMaterial(name: string, color: BABYLON.Color3, scene: BABYLON.Scene): BABYLON.PBRMaterial {
  const mat = new BABYLON.PBRMaterial(name, scene);
  mat.albedoColor = color;
  mat.roughness = 0.3; // Shiny leather/synthetic boots
  mat.metallic = 0.1;
  return mat;
}

function createHairMaterial(name: string, color: BABYLON.Color3, scene: BABYLON.Scene): BABYLON.PBRMaterial {
  const mat = new BABYLON.PBRMaterial(name, scene);
  mat.albedoColor = color;
  mat.roughness = 0.8;
  mat.metallic = 0.0;
  return mat;
}

function createEyeWhiteMaterial(scene: BABYLON.Scene): BABYLON.PBRMaterial {
  const mat = new BABYLON.PBRMaterial("eyeWhite", scene);
  mat.albedoColor = new BABYLON.Color3(0.95, 0.95, 0.95);
  mat.roughness = 0.1; // Wet eyes
  mat.metallic = 0.0;
  mat.clearCoat.isEnabled = true;
  mat.clearCoat.intensity = 1.0;
  return mat;
}

function createEyeIrisMaterial(color: BABYLON.Color3, scene: BABYLON.Scene): BABYLON.PBRMaterial {
  const mat = new BABYLON.PBRMaterial("eyeIris", scene);
  mat.albedoColor = color;
  mat.roughness = 0.2;
  mat.metallic = 0.0;
  mat.clearCoat.isEnabled = true;
  mat.clearCoat.intensity = 1.0;
  return mat;
}

function createLipMaterial(scene: BABYLON.Scene): BABYLON.PBRMaterial {
  const mat = new BABYLON.PBRMaterial("lipMat", scene);
  mat.albedoColor = new BABYLON.Color3(0.75, 0.4, 0.4);
  mat.roughness = 0.3; // Lips are slightly wet/shiny
  mat.metallic = 0.0;
  mat.clearCoat.isEnabled = true;
  mat.clearCoat.intensity = 0.5;
  return mat;
}

// ========== ANIMATOR - REALISTIC HUMAN MOTION ==========

export enum KickType {
  Normal,
  Power,
  Chip,
  Header,
  Tackle
}

export enum CelebrationType {
  None,
  ArmsOut,
  Slide,
  Jump,
  Dance
}

export class HumanoidAnimator {
  private _limbs: HumanoidLimbs;
  private _fbxAnimations: FBXAnimations | null = null;

  // Walk cycle state
  private _walkPhase = 0;
  private _walkCycleSpeed = 0;

  // Kick state
  private _kickTimer = 0;
  private _kickLeg: "left" | "right" = "right";
  private _kickType: KickType = KickType.Normal;

  // Dive/Jump state
  private _diveTimer = 0;
  private _diveDirection = 0;
  private _diveHeight = 0; // 0 = low, 1 = high

  // Celebration state
  private _celebrationTimer = 0;
  private _celebrationType: CelebrationType = CelebrationType.None;

  // Body motion
  private _bodyBob = 0;
  private _hipRotation = 0;
  private _torsoRotation = 0;
  private _shoulderRotation = 0;

  // Idle state
  private _breathPhase = 0;
  private _weightShiftPhase = 0;
  private _headLookPhase = 0;

  // Blinking state
  private _blinkTimer = 0;
  private _blinkInterval = 3 + Math.random() * 2; // Random blink every 3-5 seconds
  private _isBlinking = false;
  private _blinkDuration = 0.12; // Quick blink

  // Smooth transitions
  private _currentSpeed = 0;
  private _targetSpeed = 0;

  // Current animation state
  private _currentAnimState: "idle" | "walk" | "run" | "celebrate" = "idle";

  constructor(limbs: HumanoidLimbs) {
    this._limbs = limbs;
  }

  // Set FBX animations
  setFBXAnimations(animations: FBXAnimations): void {
    this._fbxAnimations = animations;
  }

  // Play a specific FBX animation, swapping models for celebrate
  private _playFBXAnimation(state: "idle" | "walk" | "run" | "celebrate"): void {
    if (!this._fbxAnimations) return;

    if (this._currentAnimState === state) return;
    const prevState = this._currentAnimState;
    this._currentAnimState = state;

    // Stop all animations first
    if (this._fbxAnimations.idle) this._fbxAnimations.idle.stop();
    if (this._fbxAnimations.walk) this._fbxAnimations.walk.stop();
    if (this._fbxAnimations.run) this._fbxAnimations.run.stop();
    if (this._fbxAnimations.celebrate) this._fbxAnimations.celebrate.stop();

    // MODEL SWAP: switch visibility between run and celebrate meshes
    const enteringCelebrate = state === "celebrate";
    const leavingCelebrate = prevState === "celebrate" && state !== "celebrate";
    if (enteringCelebrate || leavingCelebrate) {
      const showRun = !enteringCelebrate;
      // Toggle run model nodes — setEnabled cascades to all descendants
      for (const node of (this._fbxAnimations.runRootNodes || [])) {
        node.setEnabled(showRun);
      }
      // Toggle celebrate model nodes — setEnabled cascades to all descendants
      for (const node of (this._fbxAnimations.celebrateRootNodes || [])) {
        node.setEnabled(enteringCelebrate);
      }
      console.log(`[DEBUG] Model swap: showRun=${showRun}, showCelebrate=${enteringCelebrate}`);
    }

    // Play the requested animation
    const anim = this._fbxAnimations[state];
    if (anim) {
      anim.play(true);
    }
  }

  animate(dt: number, speed: number, isKicking: boolean, turnRate?: number): void {
    // Smooth speed transition
    this._targetSpeed = speed;
    this._currentSpeed += (this._targetSpeed - this._currentSpeed) * Math.min(1, dt * 8);

    // Handle turning - lean into turns
    const turnLean = turnRate ? -Math.sign(turnRate) * Math.min(Math.abs(turnRate) * 0.06, 0.12) : 0;
    this._torsoRotation += (turnLean - this._torsoRotation) * Math.min(1, dt * 5);

    // Use FBX animations if available
    if (this._fbxAnimations) {
      if (this._celebrationTimer > 0) {
        this._playFBXAnimation("celebrate");
        this._celebrationTimer -= dt;
      } else if (this._currentSpeed > 0.3) {
        this._playFBXAnimation("run");
      } else {
        this._playFBXAnimation("idle");
      }

      // Still update blinking for facial animation if limbs exist
      if (this._limbs) this._updateBlinking(dt);
      return;
    }

    // Skip if no limbs (procedural needs them)
    if (!this._limbs) return;

    // Fall back to procedural animations if no FBX
    if (this._celebrationTimer > 0) {
      this._animateCelebration(dt);
    } else if (this.isDiving) {
      this._animateDive(dt);
    } else if (isKicking) {
      this._animateKick(dt);
    } else if (this._currentSpeed > 0.3) {
      if (this._currentSpeed > 5) {
        this._animateSprint(dt);
      } else {
        this._animateWalk(dt);
      }
    } else {
      this._animateIdle(dt);
    }

    // Handle blinking
    this._updateBlinking(dt);

    // Apply body lean from turning (only if not diving and not celebrating)
    if (!this.isDiving && this._celebrationTimer <= 0) {
      this._limbs.torsoGroup.rotation.z = this._torsoRotation;
      this._limbs.headGroup.rotation.z = this._torsoRotation * 0.3;
    }
  }

  triggerKick(type: KickType = KickType.Normal): void {
    this._kickTimer = 0.4;
    this._kickType = type;
    this._kickLeg = this._kickLeg === "right" ? "left" : "right";
  }

  triggerCelebration(type: CelebrationType = CelebrationType.ArmsOut): void {
    this._celebrationTimer = 3.0; // 3 second celebration
    this._celebrationType = type;
  }

  /** Skip the current celebration immediately */
  _skipCelebration(): void {
    this._celebrationTimer = 0;
    // Stop celebration animation and go to idle
    if (this._fbxAnimations?.celebrate) {
      this._fbxAnimations.celebrate.stop();
    }
    // Restore run model, hide celebrate model
    for (const node of (this._fbxAnimations?.runRootNodes || [])) {
      node.setEnabled(true);
    }
    for (const node of (this._fbxAnimations?.celebrateRootNodes || [])) {
      node.setEnabled(false);
    }
    this._currentAnimState = "idle";
  }

  get isKicking(): boolean {
    return this._kickTimer > 0;
  }

  get isCelebrating(): boolean {
    return this._celebrationTimer > 0;
  }

  triggerDive(direction: number, heightFactor: number = 0): void {
    this._diveTimer = 1.0; // Matches Goalkeeper dive duration
    this._diveDirection = direction;
    this._diveHeight = heightFactor;
  }

  get isDiving(): boolean {
    return this._diveTimer > 0;
  }

  /**
   * Update blinking animation - random blinks every few seconds
   */
  private _updateBlinking(dt: number): void {
    // Don't blink while kicking or moving fast
    if (this._kickTimer > 0 || this._currentSpeed > 3) {
      // Keep eyes open
      this._setEyelidsOpen(1);
      this._blinkTimer = 0;
      return;
    }

    if (this._isBlinking) {
      // Currently blinking - update position based on progress
      const blinkProgress = 1 - (this._blinkTimer / this._blinkDuration);
      // Smooth blink curve - quick close, quick open
      const blinkAmount = Math.sin(blinkProgress * Math.PI);
      this._setEyelidsOpen(1 - blinkAmount);

      this._blinkTimer -= dt;
      if (this._blinkTimer <= 0) {
        this._isBlinking = false;
        this._blinkTimer = 0;
        this._setEyelidsOpen(1);
      }
    } else {
      // Waiting for next blink
      this._blinkTimer += dt;
      if (this._blinkTimer >= this._blinkInterval) {
        // Trigger blink
        this._isBlinking = true;
        this._blinkTimer = this._blinkDuration;
        this._blinkInterval = 3 + Math.random() * 3; // Reset interval for next blink
      }
    }
  }

  /**
   * Set eyelid openness (0 = closed, 1 = open)
   */
  private _setEyelidsOpen(openness: number): void {
    if (this._limbs.leftEyelid) {
      this._limbs.leftEyelid.position.y = 0.008 - (openness * 0.012);
      this._limbs.leftEyelid.scaling.y = 0.3 * openness + 0.01;
    }
    if (this._limbs.rightEyelid) {
      this._limbs.rightEyelid.position.y = 0.008 - (openness * 0.012);
      this._limbs.rightEyelid.scaling.y = 0.3 * openness + 0.01;
    }
  }

  /**
   * Realistic walk cycle with proper human gait phases:
   * - Heel strike (foot contacts ground)
   * - Loading response (weight transfer)
   * - Mid-stance (push off)
   * - Toe-off (foot leaves ground)
   * - Swing phase (foot moves forward)
   */
  private _animateWalk(dt: number): void {
    const speed = this._currentSpeed;

    // Walk frequency - humans typically 90-120 steps per minute when walking
    const walkFreq = 3.0 + speed * 0.2;
    this._walkPhase += dt * walkFreq;

    // Body bob - happens twice per cycle (once per footfall)
    // More pronounced for visibility
    this._bodyBob = Math.sin(this._walkPhase * 2) * 0.025 * Math.min(speed / 4, 1);

    // Hip rotation - more pronounced pelvic rotation
    this._hipRotation = Math.sin(this._walkPhase) * 0.12 * Math.min(speed / 5, 1);

    // Shoulder counter-rotation (opposite to hip)
    this._shoulderRotation = -this._hipRotation * 0.7;

    // Apply body motion with adjusted height for new proportions
    this._limbs.torsoGroup.position.y = 1.50 + this._bodyBob;
    this._limbs.torsoGroup.rotation.y = this._hipRotation;

    // Head counter-rotation to stay focused forward
    this._limbs.headGroup.rotation.y = this._shoulderRotation * 0.5;
    // Subtle head bob opposite to body
    this._limbs.headGroup.rotation.x = Math.sin(this._walkPhase * 2 + Math.PI) * 0.03 * Math.min(speed / 4, 1);

    // Calculate leg angles based on walk phase
    const leftPhase = this._walkPhase;
    const rightPhase = this._walkPhase + Math.PI;

    // Enhanced hip flexion for more natural walking
    const leftHip = this._calculateHipAngle(leftPhase, speed);
    const rightHip = this._calculateHipAngle(rightPhase, speed);

    // Enhanced knee angles
    const leftKnee = this._calculateKneeAngle(leftPhase, speed);
    const rightKnee = this._calculateKneeAngle(rightPhase, speed);

    // Apply leg animations with better mechanics
    this._applyLegAnimation(this._limbs.leftLeg, leftHip, leftKnee, leftPhase, speed);
    this._applyLegAnimation(this._limbs.rightLeg, rightHip, rightKnee, rightPhase, speed);

    // Enhanced arm swing - more natural human movement
    const armSwing = Math.sin(this._walkPhase) * 0.55 * Math.min(speed / 4, 1);
    const armBend = Math.abs(armSwing) * 0.5; // More elbow bend

    // Arms swing opposite to legs with proper timing
    this._limbs.leftArm.rotation.x = -armSwing - armBend;
    this._limbs.rightArm.rotation.x = armSwing + armBend;

    // Arm rotation at shoulder (natural twist)
    this._limbs.leftArm.rotation.y = Math.sin(this._walkPhase) * 0.2;
    this._limbs.rightArm.rotation.y = -Math.sin(this._walkPhase) * 0.2;

    // Arms slight outward angle (natural A-pose when walking)
    this._limbs.leftArm.rotation.z = 0.15 + Math.abs(armSwing) * 0.1;
    this._limbs.rightArm.rotation.z = -0.15 - Math.abs(armSwing) * 0.1;

    // Hip sway (lateral movement)
    const hipSway = Math.sin(this._walkPhase * 2) * 0.04 * Math.min(speed / 4, 1);
    this._limbs.torsoGroup.position.x = hipSway;

    // Forward lean increases slightly with speed
    const forwardLean = Math.min(speed * 0.02, 0.15);
    this._limbs.torsoGroup.rotation.x = forwardLean;
  }

  /**
   * Calculate hip angle based on walk phase - enhanced for natural movement
   */
  private _calculateHipAngle(phase: number, speed: number): number {
    const sin = Math.sin(phase);

    // Swing forward during first half (0 to PI), swing back during second half
    let swing = 0;
    if (sin > 0) {
      // Swing forward - peak at phase = PI/2
      // More range of motion for natural walking
      swing = sin * 0.6 * Math.min(speed / 4, 1.2);
    } else {
      // Push back - less than forward swing but still significant
      swing = sin * 0.4;
    }

    return swing;
  }

  /**
   * Calculate knee angle based on walk phase
   * Knees bend more during swing phase to clear ground
   */
  private _calculateKneeAngle(phase: number, speed: number): number {
    const sin = Math.sin(phase);

    // Maximum knee bend during mid-swing
    let bend = 0;
    if (sin > 0.2) {
      // Swing phase - bend knee to lift foot
      // More pronounced bend for better ground clearance
      bend = (sin - 0.2) * 1.2 * Math.min(speed / 3, 1.3);
    } else {
      // Stance phase - slight bend for shock absorption
      bend = -0.15;
    }

    return bend;
  }

  /**
   * Apply calculated hip and knee angles to leg
   */
  private _applyLegAnimation(leg: BABYLON.TransformNode, hipAngle: number, kneeAngle: number, phase: number, speed: number): void {
    // Hip rotation (forward/back swing)
    leg.rotation.x = hipAngle;

    // Knee is a child of leg pivot, but we need to simulate knee bend
    // Since we don't have direct knee access, we apply additional rotation
    // This is an approximation using the leg's z-rotation for knee effect
    leg.rotation.z = kneeAngle * 0.3;
  }

  /**
   * Sprint animation - arms pump, legs extend more
   */
  private _animateSprint(dt: number): void {
    const speed = this._currentSpeed;

    // Faster cycle during sprint
    const sprintFreq = 4.0 + (speed - 5) * 0.25;
    this._walkPhase += dt * sprintFreq;

    // More pronounced body bob during running
    this._bodyBob = Math.sin(this._walkPhase * 2) * 0.035;
    this._limbs.torsoGroup.position.y = 1.50 + this._bodyBob;

    // Lateral sway during sprint - body tilts side to side
    const lateralSway = Math.sin(this._walkPhase * 2) * 0.05;
    this._limbs.torsoGroup.position.x = lateralSway;

    // Forward lean increases significantly when sprinting
    const forwardLean = 0.15 + (speed - 5) * 0.02;
    this._limbs.torsoGroup.rotation.x = Math.min(forwardLean, 0.35);

    // Arms pump higher when running with elbow bend effect
    const armPump = Math.sin(this._walkPhase) * 0.85;
    const armBend = Math.abs(armPump) * 0.6;
    this._limbs.leftArm.rotation.x = -armPump - armBend;
    this._limbs.rightArm.rotation.x = armPump + armBend;

    // Arm rotation for natural sprint form
    this._limbs.leftArm.rotation.y = Math.sin(this._walkPhase) * 0.25;
    this._limbs.rightArm.rotation.y = -Math.sin(this._walkPhase) * 0.25;

    // Arms closer to body when sprinting
    this._limbs.leftArm.rotation.z = 0.25 + Math.abs(armPump) * 0.15;
    this._limbs.rightArm.rotation.z = -0.25 - Math.abs(armPump) * 0.15;

    // Hip rotation during sprint
    this._hipRotation = Math.sin(this._walkPhase) * 0.15;
    this._limbs.torsoGroup.rotation.y = this._hipRotation;

    // Legs with enhanced swing
    const leftPhase = this._walkPhase;
    const rightPhase = this._walkPhase + Math.PI;

    const leftHip = this._calculateHipAngle(leftPhase, speed) * 1.3;
    const rightHip = this._calculateHipAngle(rightPhase, speed) * 1.3;

    const leftKnee = this._calculateKneeAngle(leftPhase, speed);
    const rightKnee = this._calculateKneeAngle(rightPhase, speed);

    this._applyLegAnimation(this._limbs.leftLeg, leftHip, leftKnee, leftPhase, speed);
    this._applyLegAnimation(this._limbs.rightLeg, rightHip, rightKnee, rightPhase, speed);

    // Head stays focused forward
    this._limbs.headGroup.rotation.y = -lateralSway * 0.5;
    this._limbs.headGroup.rotation.x = Math.sin(this._walkPhase * 2 + Math.PI) * 0.05;
  }

  /**
   * Idle animation - natural breathing and subtle weight shift
   */
  private _animateIdle(dt: number): void {
    // Smoothly return from any previous motion
    const returnSpeed = Math.min(1, dt * 4);

    // Body bob returns to 0
    this._bodyBob *= (1 - returnSpeed);
    this._limbs.torsoGroup.position.y = 1.50 + this._bodyBob;

    // Torso position x returns to 0 (from lateral sway)
    this._limbs.torsoGroup.position.x *= (1 - returnSpeed);

    // Torso rotation returns to 0
    this._torsoRotation *= (1 - returnSpeed);
    this._limbs.torsoGroup.rotation.x = this._torsoRotation * 0.5;
    this._limbs.torsoGroup.rotation.y = 0;
    this._limbs.torsoGroup.rotation.z *= (1 - returnSpeed);

    this._limbs.headGroup.rotation.y *= (1 - returnSpeed);
    this._limbs.headGroup.rotation.z *= (1 - returnSpeed);

    // Legs return to neutral
    this._limbs.leftLeg.rotation.x *= (1 - returnSpeed);
    this._limbs.rightLeg.rotation.x *= (1 - returnSpeed);
    this._limbs.leftLeg.rotation.z *= (1 - returnSpeed);
    this._limbs.rightLeg.rotation.z *= (1 - returnSpeed);

    // Arms hang naturally with slight outward angle
    const armReturn = Math.min(1, dt * 3);
    this._limbs.leftArm.rotation.x = this._limbs.leftArm.rotation.x * (1 - armReturn) + 0.05;
    this._limbs.rightArm.rotation.x = this._limbs.rightArm.rotation.x * (1 - armReturn) - 0.05;
    this._limbs.leftArm.rotation.y *= (1 - armReturn);
    this._limbs.rightArm.rotation.y *= (1 - armReturn);
    // Natural A-pose when idle
    this._limbs.leftArm.rotation.z = this._limbs.leftArm.rotation.z * (1 - armReturn) + 0.1;
    this._limbs.rightArm.rotation.z = this._limbs.rightArm.rotation.z * (1 - armReturn) - 0.1;

    // Breathing animation - chest expands
    this._breathPhase += dt * 0.8; // ~12 breaths per minute
    const breathAmount = Math.sin(this._breathPhase) * 0.01;

    // Subtle weight shift - side to side
    this._weightShiftPhase += dt * 0.3;
    const weightShift = Math.sin(this._weightShiftPhase) * 0.02;

    // Apply subtle idle motions to torso
    this._limbs.torsoGroup.position.y += breathAmount;
    this._limbs.torsoGroup.rotation.z = weightShift;

    // Head subtle movement - looking around occasionally
    this._headLookPhase += dt * 0.15;
    const headMove = Math.sin(this._headLookPhase) * 0.03;
    this._limbs.headGroup.rotation.y = headMove;
    // Subtle head tilt
    this._limbs.headGroup.rotation.z = Math.sin(this._headLookPhase * 0.7) * 0.015;
  }

  /**
   * Dive animation - horizontal lunge for goalkeepers
   */
  private _animateDive(dt: number): void {
    this._diveTimer -= dt;
    const duration = 1.0;
    const t = 1 - (this._diveTimer / duration);
    const dir = this._diveDirection;
    const isHigh = this._diveHeight > 0.5;

    // Reset animations
    this._limbs.leftLeg.rotation.set(0, 0, 0);
    this._limbs.rightLeg.rotation.set(0, 0, 0);
    this._limbs.leftArm.rotation.set(0, 0, 0);
    this._limbs.rightArm.rotation.set(0, 0, 0);

    if (t < 0.4) {
      // Lunge phase (0.0 - 0.4)
      const p = t / 0.4;
      const ease = p * (2 - p); // Ease out quad

      // Rotate body sideways
      this._limbs.torsoGroup.rotation.z = -dir * ease * Math.PI * 0.45;

      if (isHigh) {
        // High save: torso stays up or jumps slightly
        this._limbs.torsoGroup.position.y = 1.45 + ease * 0.4;
        // Reach arms UP
        this._limbs.leftArm.rotation.z = -dir * ease * 2.5;
        this._limbs.rightArm.rotation.z = -dir * ease * 2.5;
      } else {
        // Low save: lower body to ground
        this._limbs.torsoGroup.position.y = 1.45 - ease * 1.1;
        // Reach arms out
        this._limbs.leftArm.rotation.z = -dir * ease * 1.2;
        this._limbs.rightArm.rotation.z = -dir * ease * 1.2;
      }

      // Extend legs
      this._limbs.leftLeg.rotation.z = -dir * ease * 0.3;
      this._limbs.rightLeg.rotation.z = -dir * ease * 0.3;

    } else if (t < 0.7) {
      // Hold phase (0.4 - 0.7)
      this._limbs.torsoGroup.rotation.z = -dir * Math.PI * 0.45;
      this._limbs.torsoGroup.position.y = isHigh ? 1.85 : 0.35;

      const armAngle = isHigh ? 2.5 : 1.2;
      this._limbs.leftArm.rotation.z = -dir * armAngle;
      this._limbs.rightArm.rotation.z = -dir * armAngle;

    } else {
      // Recovery phase (0.7 - 1.0)
      const p = (t - 0.7) / 0.3;
      const ease = p * p; // Ease in quad

      const startY = isHigh ? 1.85 : 0.35;
      const endY = 1.45;

      this._limbs.torsoGroup.rotation.z = -dir * (1 - ease) * Math.PI * 0.45;
      this._limbs.torsoGroup.position.y = startY + (endY - startY) * ease;

      const armAngle = isHigh ? 2.5 : 1.2;
      this._limbs.leftArm.rotation.z = -dir * (1 - ease) * armAngle;
      this._limbs.rightArm.rotation.z = -dir * (1 - ease) * armAngle;
    }
  }

  /**
   * Realistic soccer kick motion with variations for different kick types
   */
  private _animateKick(dt: number): void {
    this._kickTimer -= dt;
    const duration = 0.4;
    const t = 1 - (this._kickTimer / duration); // 0 to 1

    const kickLeg = this._kickLeg === "right" ? this._limbs.rightLeg : this._limbs.leftLeg;
    const standLeg = this._kickLeg === "right" ? this._limbs.leftLeg : this._limbs.rightLeg;
    const kickArm = this._kickLeg === "right" ? this._limbs.rightArm : this._limbs.leftArm;
    const otherArm = this._kickLeg === "right" ? this._limbs.leftArm : this._limbs.rightArm;

    const isPower = this._kickType === KickType.Power;
    const isChip = this._kickType === KickType.Chip;
    const isHeader = this._kickType === KickType.Header;

    if (isHeader) {
      this._animateHeader(t);
      return;
    }

    if (t < 0.2) {
      // Wind up
      const progress = t / 0.2;
      const windUpPower = isPower ? 0.8 : 0.4;

      kickLeg.rotation.x = -progress * windUpPower;
      kickLeg.rotation.z = progress * 0.3;
      standLeg.rotation.x = -0.05;

      const armFactor = isPower ? 1.5 : 1.0;
      kickArm.rotation.x = progress * 0.3 * armFactor;
      otherArm.rotation.x = -progress * 0.2 * armFactor;

      this._limbs.torsoGroup.rotation.y = progress * 0.2;
      if (isChip) this._limbs.torsoGroup.rotation.x = -progress * 0.15;

    } else if (t < 0.5) {
      // Forward swing
      const progress = (t - 0.2) / 0.3;
      const swingPower = isPower ? 1.6 : 1.2;

      kickLeg.rotation.x = (isPower ? -0.8 : -0.4) + progress * swingPower;
      kickLeg.rotation.z = 0.3 - progress * 0.3;
      standLeg.rotation.x = -0.05 + progress * 0.1;

      kickArm.rotation.x = 0.3 - progress * 0.6;
      otherArm.rotation.x = -0.2 - progress * 0.4;

      this._limbs.torsoGroup.rotation.y = 0.2 - progress * 0.5;
      this._limbs.torsoGroup.rotation.x = isChip ? -0.15 : progress * 0.2;

    } else if (t < 0.7) {
      // Contact
      kickLeg.rotation.x = isPower ? 1.1 : 0.8;
      kickLeg.rotation.z = 0;
      standLeg.rotation.x = 0.05;
      this._limbs.torsoGroup.rotation.x = isChip ? -0.15 : 0.2;
    } else {
      // Follow through
      const progress = (t - 0.7) / 0.3;
      const ease = 1 - Math.pow(1 - progress, 3);
      kickLeg.rotation.x *= (1 - ease);
      standLeg.rotation.x *= (1 - ease);
      this._limbs.torsoGroup.rotation.x *= (1 - ease);
      this._limbs.torsoGroup.rotation.y *= (1 - ease);
    }
  }

  private _animateHeader(t: number): void {
    if (t < 0.3) {
      const p = t / 0.3;
      this._limbs.torsoGroup.rotation.x = -p * 0.4;
      this._limbs.headGroup.rotation.x = -p * 0.3;
    } else if (t < 0.7) {
      const p = (t - 0.3) / 0.4;
      this._limbs.torsoGroup.rotation.x = -0.4 + p * 0.8;
      this._limbs.headGroup.rotation.x = -0.3 + p * 0.8;
    } else {
      const p = (t - 0.7) / 0.3;
      this._limbs.torsoGroup.rotation.x = 0.4 * (1 - p);
      this._limbs.headGroup.rotation.x = 0.5 * (1 - p);
    }
  }

  private _animateCelebration(dt: number): void {
    this._celebrationTimer -= dt;
    const time = 3.0 - this._celebrationTimer;

    switch (this._celebrationType) {
      case CelebrationType.ArmsOut:
        this._animateWalk(dt);
        this._limbs.leftArm.rotation.z = 1.2 + Math.sin(time * 5) * 0.2;
        this._limbs.rightArm.rotation.z = -1.2 - Math.sin(time * 5) * 0.2;
        break;
      case CelebrationType.Slide:
        if (time < 1.5) {
          const p = time / 1.5;
          this._limbs.torsoGroup.position.y = 1.45 - p * 1.1;
          this._limbs.torsoGroup.rotation.x = -p * 0.3;
          this._limbs.leftLeg.rotation.x = 1.2;
          this._limbs.rightLeg.rotation.x = 1.2;
        }
        break;
      case CelebrationType.Jump:
        const jumpH = Math.max(0, Math.sin(time * 4) * 0.8);
        this._limbs.torsoGroup.position.y = 1.45 + jumpH;
        this._limbs.rightArm.rotation.x = -2.5;
        break;
      case CelebrationType.Dance:
        const tempo = 10;
        const phase = time % 3.0; // 3 second loop or distinct phases

        if (phase < 1.5) {
          // Phase 1: High-energy hopping and arm waving
          const beat = Math.sin(time * tempo);
          const fastBeat = Math.sin(time * tempo * 2);

          const hop = Math.max(0, fastBeat) * 0.15;
          this._limbs.torsoGroup.position.y = 1.45 + hop;
          this._limbs.torsoGroup.rotation.y = beat * 0.5;

          const armWave = Math.cos(time * tempo) * 1.5;
          this._limbs.leftArm.rotation.x = -1.5 + armWave;
          this._limbs.rightArm.rotation.x = -1.5 - armWave;
          this._limbs.leftArm.rotation.z = 0.8 + beat * 0.4;
          this._limbs.rightArm.rotation.z = -0.8 + beat * 0.4;

          const legLift = Math.abs(beat) * 0.8;
          if (beat > 0) {
            this._limbs.leftLeg.rotation.x = legLift;
            this._limbs.rightLeg.rotation.x = 0;
          } else {
            this._limbs.leftLeg.rotation.x = 0;
            this._limbs.rightLeg.rotation.x = legLift;
          }
          this._limbs.headGroup.rotation.x = fastBeat * 0.2;
        } else {
          // Phase 2: Smooth side-to-side groove
          const slowTempo = 5;
          const sway = Math.sin(time * slowTempo);

          this._limbs.torsoGroup.position.y = 1.45;
          this._limbs.torsoGroup.rotation.z = sway * 0.2;
          this._limbs.torsoGroup.rotation.y = sway * 0.4;

          // Arms swaying side to side
          this._limbs.leftArm.rotation.z = 1.0 + sway * 0.5;
          this._limbs.rightArm.rotation.z = -1.0 + sway * 0.5;
          this._limbs.leftArm.rotation.x = -0.5;
          this._limbs.rightArm.rotation.x = -0.5;

          this._limbs.leftLeg.rotation.x = -0.1;
          this._limbs.rightLeg.rotation.x = -0.1;
          this._limbs.headGroup.rotation.z = -sway * 0.2;
        }
        break;
    }
  }
}
